﻿// src/hos/hosStore.tsx
import React, { createContext, useContext, useMemo, useRef, useState } from "react";
import { DEFAULT_ACCESS_CONTROL_CATALOG } from "../keycoder/application/accessControlCatalog.ts";
import type { AccessControlCatalog } from "../keycoder/domain/accessControl.ts";
import type { EncoderDevice, Keycard, KeycardOperationLog } from "../keycoder/domain/entities.ts";
import type {
  Adjustment,
  BillingAuditEntry,
  BillingInvoice,
  ChannelConnection,
  ChannelReservationRecord,
  ChannelSyncAttempt,
  FiscalDocument,
  HousekeepingAuditEntry,
  HousekeepingRealtimeEnvelope,
  HousekeepingTask,
  IntegrationConnection,
  IntegrationLogEntry,
  IntegrationOperationRecord,
  MaintenanceReport,
  RoomBlock,
  FrontDeskAuditEntry,
  FrontDeskInvoice,
  FrontDeskPaymentSettlement,
  FrontDeskRoomMove,
  Folio,
  Guest,
  GuestNote,
  GuestNoteDepartment,
  GuestNoteScope,
  GroupEvent,
  GroupReservation,
  GroupReservationCreateInput,
  GroupReservationUpdateInput,
  HosEvent,
  HosEventType,
  HosState,
  KeyCredential,
  ParkingSession,
  Payment,
  Posting,
  Refund,
  ReservationAlert,
  ReservationAuditEntry,
  Reservation,
  ReservationCancellationInput,
  ReservationConfirmation,
  ReservationDeposit,
  ReservationUpdateInput,
  ReservationUpdateSnapshot,
  Room,
  RoomType,
  Stay,
  WorkOrder,
  WorkOrderPriority,
  WorkOrderStatus,
} from "./hosTypes";
import { addReservationHistoryEntry, buildCancelledReservation } from "./historyService";
import { createGroupBillingAccount } from "./billingService";
import type {
  CreateAdjustmentRequestDto,
  CreateBillingFolioRequestDto,
  CreateRefundRequestDto,
  GenerateBillingInvoiceRequestDto,
  PostFolioChargeRequestDto,
  SettleFolioBalanceRequestDto,
} from "./billing-management/contracts/dtos.ts";
import { billingManagementEndpoints } from "./billing-management/endpoints.ts";
import { InMemoryFiscalIntegrationProvider } from "./billing-management/fiscal.ts";
import { InMemoryBillingInvoiceProvider } from "./billing-management/invoice.ts";
import type {
  ImportChannelReservationRequestDto,
  SyncChannelRatesAvailabilityRequestDto,
} from "./channel-management/contracts/dtos.ts";
import { channelManagementEndpoints } from "./channel-management/endpoints.ts";
import { createMockChannelProvider, type ChannelProvider } from "./channel-management/provider.ts";
import { normalizeMandatoryGuestInfo } from "./guestValidation";
import type {
  EnrollGuestLoyaltyRequestDto,
  MergeGuestProfilesRequestDto,
  UpdateGuestContactDetailsRequestDto,
  UpdateGuestMarketingRequestDto,
  UpsertGuestPreferenceRequestDto,
} from "./guest-profile-management/contracts/dtos.ts";
import { guestProfileManagementEndpoints } from "./guest-profile-management/endpoints.ts";
import { syncGuestProfile } from "./guest-profile-management/service.ts";
import type { IntegrationAdapter } from "./integrations/adapters.ts";
import { integrationEndpoints } from "./integrations/endpoints.ts";
import {
  createMockChannelManagerAdapter,
  createMockFiscalPrinterAdapter,
  createMockHousekeepingAppAdapter,
  createMockIdScannerAdapter,
  createMockKeyCardAdapter,
  createMockPaymentGatewayAdapter,
  createMockPosAdapter,
  createMockRevenueManagementAdapter,
} from "./integrations/mocks.ts";
import type {
  BlockRoomRequestDto,
  CreateHousekeepingTaskRequestDto,
  ReleaseRoomBlockRequestDto,
  ReportMaintenanceIssueRequestDto,
  UpdateHousekeepingTaskRequestDto,
  UpdateMaintenanceReportRequestDto,
  UpdateRoomStateRequestDto,
} from "./housekeeping/contracts/dtos.ts";
import { housekeepingEndpoints } from "./housekeeping/endpoints.ts";
import { InMemoryHousekeepingRealtimeGateway } from "./housekeeping/realtime.ts";
import { applyRoomState } from "./housekeeping/roomState.ts";
import type {
  CompleteCheckoutRequestDto,
  ExecuteCheckinRequestDto,
  MoveStayRoomRequestDto,
  RegisterGuestDocumentRequestDto,
} from "./front-desk/contracts/dtos.ts";
import { frontDeskEndpoints } from "./front-desk/endpoints.ts";
import { InMemoryFrontDeskInvoiceProvider } from "./front-desk/invoice.ts";
import { InMemoryFrontDeskKeycardProvider } from "./front-desk/keycard.ts";
import type {
  CreateManagedReservationRequestDto,
  CreateManagedReservationResponseDto,
  CreateWaitlistEntryRequestDto,
  CreateWaitlistEntryResponseDto,
  PromoteWaitlistEntryRequestDto,
  PromoteWaitlistEntryResponseDto,
  RecordReservationDepositRequestDto,
  RecordReservationDepositResponseDto,
  SendReservationConfirmationRequestDto,
  SendReservationConfirmationResponseDto,
  UpdateOverbookingSettingsRequestDto,
} from "./reservation-management/contracts/dtos.ts";
import { InMemoryReservationConfirmationEmailProvider } from "./reservation-management/email.ts";
import { reservationManagementEndpoints } from "./reservation-management/endpoints.ts";

type HosActions = {
  upsertGuest: (input: Pick<Guest, "firstName" | "lastName" | "email"> & Partial<Guest>) => Guest;
  updateGuest: (guestId: string, input: Partial<Guest>) => Guest | null;
  addGuestNote: (input: {
    guestId: string;
    title: string;
    body: string;
    author: string;
    department?: GuestNoteDepartment;
    scope?: GuestNoteScope;
    reservationId?: string;
    stayId?: string;
    pinned?: boolean;
  }) => GuestNote | null;
  updateGuestContactDetails: (input: UpdateGuestContactDetailsRequestDto) => { ok: boolean; code?: string; message?: string; fieldErrors?: Record<string, string>; guest?: Guest; };
  upsertGuestPreference: (input: UpsertGuestPreferenceRequestDto) => { ok: boolean; code?: string; message?: string; fieldErrors?: Record<string, string>; guest?: Guest; };
  enrollGuestLoyalty: (input: EnrollGuestLoyaltyRequestDto) => { ok: boolean; code?: string; message?: string; fieldErrors?: Record<string, string>; guest?: Guest; };
  updateGuestMarketing: (input: UpdateGuestMarketingRequestDto) => { ok: boolean; code?: string; message?: string; fieldErrors?: Record<string, string>; guest?: Guest; };
  mergeGuestProfiles: (input: MergeGuestProfilesRequestDto) => { ok: boolean; code?: string; message?: string; fieldErrors?: Record<string, string>; targetGuest?: Guest; sourceGuest?: Guest; };
  runIntegrationHealthCheck: (connectionId: string) => Promise<{ ok: boolean; message?: string; connection?: IntegrationConnection; logs?: IntegrationLogEntry[]; }>;
  runIntegrationMockOperation: (connectionId: string) => Promise<{ ok: boolean; message?: string; connection?: IntegrationConnection; operation?: IntegrationOperationRecord; logs?: IntegrationLogEntry[]; }>;
  createReservation: (input: Omit<Reservation, "id" | "createdAt" | "status" | "reservationType" | "billingMode"> & Partial<Pick<Reservation, "reservationType" | "billingMode">>) => Reservation;
  createManagedReservation: (input: CreateManagedReservationRequestDto) => Promise<CreateManagedReservationResponseDto>;
  recordReservationDeposit: (input: RecordReservationDepositRequestDto) => RecordReservationDepositResponseDto;
  sendReservationConfirmation: (input: SendReservationConfirmationRequestDto) => Promise<SendReservationConfirmationResponseDto>;
  createWaitlistEntry: (input: CreateWaitlistEntryRequestDto) => CreateWaitlistEntryResponseDto;
  promoteWaitlistEntry: (input: PromoteWaitlistEntryRequestDto) => Promise<PromoteWaitlistEntryResponseDto>;
  acknowledgeReservationAlert: (alertId: string) => void;
  updateOverbookingSettings: (input: UpdateOverbookingSettingsRequestDto) => void;
  registerGuestDocument: (input: RegisterGuestDocumentRequestDto) => { ok: boolean; message?: string; fieldErrors?: Record<string, string>; document?: import("./front-desk/types.ts").GuestDocumentRecord; auditEntry?: FrontDeskAuditEntry; };
  executeFrontDeskCheckin: (input: ExecuteCheckinRequestDto) => Promise<{ ok: boolean; message?: string; stay?: Stay; roomId?: string; key?: KeyCredential | null; auditEntries?: FrontDeskAuditEntry[]; fieldErrors?: Record<string, string>; }>;
  moveStayRoom: (input: MoveStayRoomRequestDto) => Promise<{ ok: boolean; message?: string; auditEntries?: FrontDeskAuditEntry[]; roomMove?: FrontDeskRoomMove; key?: KeyCredential | null; fieldErrors?: Record<string, string>; }>;
  completeFrontDeskCheckout: (input: CompleteCheckoutRequestDto) => Promise<{ ok: boolean; message?: string; invoice?: FrontDeskInvoice; settlement?: FrontDeskPaymentSettlement; auditEntries?: FrontDeskAuditEntry[]; fieldErrors?: Record<string, string>; }>;
  createGroupReservation: (input: GroupReservationCreateInput) => GroupReservation;
  updateGroupReservation: (reservationId: string, input: GroupReservationUpdateInput) => ReservationUpdateSnapshot | null;
  updateReservation: (reservationId: string, input: ReservationUpdateInput) => ReservationUpdateSnapshot | null;
  cancelReservation: (reservationId: string, input: ReservationCancellationInput) => ReservationUpdateSnapshot | null;
  applyEarlyCheckIn: (reservationId: string, input: { requestedTime: string; fee: number; actor?: string; note?: string; regularCheckInTime: string; }) => ReservationUpdateSnapshot | null;
  applyLateCheckOut: (stayId: string, input: { extendedDepartureTime: string; fee: number; actor?: string; note?: string; regularCheckOutTime: string; }) => ReservationUpdateSnapshot | null;
  createRoomType: (input: Pick<RoomType, "name" | "capacity" | "availability"> & Partial<Pick<RoomType, "shortCode" | "description">>) => RoomType;
  updateRoomType: (roomTypeId: string, input: Partial<Omit<RoomType, "id">>) => RoomType | null;
  assignReservationRoom: (reservationId: string, roomId: string, groupRoomId?: string) => ReservationUpdateSnapshot | null;
  restoreReservationSnapshot: (snapshot: ReservationUpdateSnapshot) => void;
  startCheckin: (reservationId: string) => void;
  completeCheckin: (reservationId: string, roomId: string) => Stay | null;
  checkoutStay: (stayId: string) => void;
  issueKey: (stayId: string) => KeyCredential | null;
  revokeKeysByStay: (stayId: string) => void;
  applyKeycoderState: (input: {
    keycards?: Keycard[];
    operationLogs?: KeycardOperationLog[];
    encoderDevices?: EncoderDevice[];
  }) => void;
  appendKeycoderOperationLog: (log: KeycardOperationLog) => void;
  setKeycoderAccessCatalog: (catalog: AccessControlCatalog) => void;
  createParkingSession: (input: Omit<ParkingSession, "id" | "status" | "postedToFolio" | "paid">) => ParkingSession;
  closeParkingSession: (sessionId: string) => void;
  createHousekeepingTask: (input: CreateHousekeepingTaskRequestDto) => { ok: boolean; message?: string; fieldErrors?: Record<string, string>; task?: HousekeepingTask; auditEntry?: HousekeepingAuditEntry; realtimeEnvelope?: HousekeepingRealtimeEnvelope; };
  updateHousekeepingTask: (input: UpdateHousekeepingTaskRequestDto) => { ok: boolean; code?: string; message?: string; fieldErrors?: Record<string, string>; task?: HousekeepingTask; room?: Room; auditEntry?: HousekeepingAuditEntry; realtimeEnvelope?: HousekeepingRealtimeEnvelope; };
  updateRoomState: (input: UpdateRoomStateRequestDto) => { ok: boolean; code?: string; message?: string; fieldErrors?: Record<string, string>; room?: Room; auditEntry?: HousekeepingAuditEntry; realtimeEnvelope?: HousekeepingRealtimeEnvelope; };
  reportMaintenanceIssue: (input: ReportMaintenanceIssueRequestDto) => { ok: boolean; code?: string; message?: string; fieldErrors?: Record<string, string>; report?: MaintenanceReport; room?: Room; auditEntry?: HousekeepingAuditEntry; realtimeEnvelope?: HousekeepingRealtimeEnvelope; };
  updateMaintenanceReport: (input: UpdateMaintenanceReportRequestDto) => { ok: boolean; code?: string; message?: string; fieldErrors?: Record<string, string>; report?: MaintenanceReport; room?: Room; auditEntry?: HousekeepingAuditEntry; realtimeEnvelope?: HousekeepingRealtimeEnvelope; };
  blockRoom: (input: BlockRoomRequestDto) => { ok: boolean; code?: string; message?: string; fieldErrors?: Record<string, string>; block?: RoomBlock; room?: Room; auditEntry?: HousekeepingAuditEntry; realtimeEnvelope?: HousekeepingRealtimeEnvelope; };
  releaseRoomBlock: (input: ReleaseRoomBlockRequestDto) => { ok: boolean; code?: string; message?: string; fieldErrors?: Record<string, string>; block?: RoomBlock; room?: Room; auditEntry?: HousekeepingAuditEntry; realtimeEnvelope?: HousekeepingRealtimeEnvelope; };
  createBillingFolio: (input: CreateBillingFolioRequestDto) => { ok: boolean; code?: string; message?: string; fieldErrors?: Record<string, string>; folio?: Folio; auditEntries?: BillingAuditEntry[]; };
  postBillingCharge: (input: PostFolioChargeRequestDto) => { ok: boolean; code?: string; message?: string; fieldErrors?: Record<string, string>; folio?: Folio; item?: Posting; auditEntries?: BillingAuditEntry[]; };
  settleBillingFolio: (input: SettleFolioBalanceRequestDto) => { ok: boolean; code?: string; message?: string; fieldErrors?: Record<string, string>; folio?: Folio; payments?: Payment[]; auditEntries?: BillingAuditEntry[]; };
  createBillingRefund: (input: CreateRefundRequestDto) => { ok: boolean; code?: string; message?: string; fieldErrors?: Record<string, string>; folio?: Folio; refund?: Refund; payment?: Payment; auditEntries?: BillingAuditEntry[]; };
  createBillingAdjustment: (input: CreateAdjustmentRequestDto) => { ok: boolean; code?: string; message?: string; fieldErrors?: Record<string, string>; folio?: Folio; adjustment?: Adjustment; item?: Posting; auditEntries?: BillingAuditEntry[]; };
  generateBillingInvoice: (input: GenerateBillingInvoiceRequestDto) => Promise<{ ok: boolean; code?: string; message?: string; fieldErrors?: Record<string, string>; invoice?: BillingInvoice; fiscalDocument?: FiscalDocument; auditEntries?: BillingAuditEntry[]; }>;
  pullChannelReservations: (connectionId: string, actor?: string) => Promise<{ ok: boolean; imported: number; skipped: number; failed: number; message?: string; }>;
  importChannelReservation: (input: ImportChannelReservationRequestDto) => { ok: boolean; code?: string; message?: string; fieldErrors?: Record<string, string>; decision?: import("./channel-management/types").ChannelImportDecision; reservation?: Reservation; link?: ChannelReservationRecord | null; };
  syncChannelRatesAndAvailability: (input: SyncChannelRatesAvailabilityRequestDto) => Promise<{ ok: boolean; code?: string; message?: string; fieldErrors?: Record<string, string>; attempt?: ChannelSyncAttempt; remoteReference?: string; delayed?: boolean; }>;
  createWorkOrder: (input: Omit<WorkOrder, "id" | "status" | "slaHours" | "createdAt" | "dueAt"> & { priority: WorkOrderPriority }) => WorkOrder;
  updateWorkOrderStatus: (id: string, status: WorkOrderStatus) => void;
  assignWorkOrder: (id: string, tech: string) => void;
  postFolio: (input: Omit<Posting, "id" | "postedAt" | "status">) => Posting;
  postParkingToFolio: (sessionId: string, folioId: string) => void;
  addPayment: (input: Omit<Payment, "id" | "ts" | "status" | "allocatedAmount" | "refundedAmount">) => Payment;
  setRoomStatus: (roomId: string, status: Room["status"]) => void;
};

type HosContextValue = {
  state: HosState;
  actions: HosActions;
  emit: (type: HosEventType, payload?: Record<string, unknown>) => void;
};

const HosContext = createContext<HosContextValue | null>(null);

const nowIso = () => new Date().toISOString();
const addHoursIso = (hours: number) => new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
const id = (prefix: string) => `${prefix}-${Math.floor(1000 + Math.random() * 8999)}`;
const reservationConfirmationProvider = new InMemoryReservationConfirmationEmailProvider();
const frontDeskKeycardProvider = new InMemoryFrontDeskKeycardProvider();
const frontDeskInvoiceProvider = new InMemoryFrontDeskInvoiceProvider();
const billingInvoiceProvider = new InMemoryBillingInvoiceProvider();
const fiscalIntegrationProvider = new InMemoryFiscalIntegrationProvider();
const housekeepingRealtimeGateway = new InMemoryHousekeepingRealtimeGateway();
const channelProviders: Record<string, ChannelProvider> = {
  "conn-booking": createMockChannelProvider("booking_com", "Booking.com", [
    {
      externalReservationId: "EXT-9101",
      guestLabel: "Nora OTA",
      guestEmail: "nora@example.com",
      arrival: "2026-04-10",
      departure: "2026-04-12",
      roomTypeId: "rt-std",
      ratePlanId: "bar",
      rooms: 1,
      adults: 2,
      children: 0,
      sourceLabel: "Booking.com",
      status: "new",
    },
  ]),
  "conn-expedia": createMockChannelProvider("expedia", "Expedia", [], { delayNextSync: true }),
};

function mapKeycoderKeycardsToHosKeys(keycards: Keycard[]): KeyCredential[] {
  return keycards
    .filter(
      (keycard): keycard is Keycard & { stayId: string; guestId: string; roomId: string } =>
        keycard.holderType === "guest" &&
        !!keycard.stayId &&
        !!keycard.guestId &&
        !!keycard.roomId
    )
    .map((keycard) => ({
      id: keycard.id,
      stayId: keycard.stayId,
      guestId: keycard.guestId,
      roomId: keycard.roomId,
      status:
        keycard.status === "active" ||
        keycard.status === "issued" ||
        keycard.status === "encoded" ||
        keycard.status === "pending_encoding"
          ? "active"
          : "revoked",
      validFrom: keycard.validFrom,
      validTo: keycard.validTo,
    }));
}
const integrationAdapters: IntegrationAdapter[] = [
  createMockPosAdapter("mock-pos", "Mock POS"),
  createMockPaymentGatewayAdapter("mock-pay", "Mock Payments"),
  createMockKeyCardAdapter("mock-key", "Mock Key Cards"),
  createMockChannelManagerAdapter("mock-channel", "Mock Channel Manager", { degraded: true }),
  createMockRevenueManagementAdapter("mock-rms", "Mock RMS"),
  createMockHousekeepingAppAdapter("mock-hk", "Mock Housekeeping"),
  createMockFiscalPrinterAdapter("mock-fiscal", "Mock Fiscal Printer"),
  createMockIdScannerAdapter("mock-id", "Mock ID Scanner"),
];

const SLA_BY_PRIORITY: Record<WorkOrderPriority, number> = {
  low: 72,
  medium: 48,
  high: 24,
  critical: 4,
};

function createGuestNote(input: {
  guestId: string;
  title: string;
  body: string;
  author: string;
  department?: GuestNoteDepartment;
  scope?: GuestNoteScope;
  reservationId?: string;
  stayId?: string;
  pinned?: boolean;
}): GuestNote {
  return {
    id: id("guest-note"),
    guestId: input.guestId,
    title: input.title.trim(),
    body: input.body.trim(),
    author: input.author.trim(),
    department: input.department ?? "general",
    scope: input.scope ?? "global",
    reservationId: input.reservationId,
    stayId: input.stayId,
    pinned: input.pinned ?? false,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  };
}

const seedRoomTypes: RoomType[] = [
  { id: "rt-std", name: "Standard", shortCode: "STD", capacity: 2, availability: "free" },
  { id: "rt-dlx", name: "Deluxe", shortCode: "DLX", capacity: 3, availability: "tight" },
  { id: "rt-ste", name: "Suite", shortCode: "STE", capacity: 4, availability: "risk" },
];

const seedRooms: Room[] = [
  {
    id: "room-214",
    number: "214",
    typeId: "rt-std",
    housekeepingStatus: "dirty",
    availabilityStatus: "available",
    status: "dirty",
    revision: 1,
  },
  {
    id: "room-307",
    number: "307",
    typeId: "rt-dlx",
    housekeepingStatus: "inspected",
    availabilityStatus: "available",
    status: "inspected",
    revision: 1,
  },
  {
    id: "room-402",
    number: "402",
    typeId: "rt-dlx",
    housekeepingStatus: "dirty",
    availabilityStatus: "out_of_order",
    status: "out_of_order",
    revision: 2,
    activeMaintenanceId: "MR-4020",
  },
  {
    id: "room-318",
    number: "318",
    typeId: "rt-std",
    housekeepingStatus: "clean",
    availabilityStatus: "blocked",
    status: "blocked",
    revision: 2,
    activeBlockId: "RB-3180",
  },
  {
    id: "room-510",
    number: "510",
    typeId: "rt-ste",
    housekeepingStatus: "clean",
    availabilityStatus: "available",
    status: "clean",
    revision: 1,
  },
];

const seedHousekeepingTasks: HousekeepingTask[] = [
  {
    id: "HK-2140",
    roomId: "room-214",
    taskType: "cleaning",
    status: "in_progress",
    priority: "high",
    assignedTo: "Eva",
    createdAt: nowIso(),
    updatedAt: nowIso(),
    actor: "Housekeeping Supervisor",
  },
  {
    id: "HK-3070",
    roomId: "room-307",
    taskType: "inspection",
    status: "verified",
    priority: "medium",
    assignedTo: "Lena",
    createdAt: nowIso(),
    updatedAt: nowIso(),
    completedAt: nowIso(),
    actor: "Housekeeping Supervisor",
  },
];

const seedMaintenanceReports: MaintenanceReport[] = [
  {
    id: "MR-4020",
    roomId: "room-402",
    title: "Fan coil fault",
    description: "Cooling unit does not start.",
    severity: "critical",
    status: "in_progress",
    createdAt: nowIso(),
    updatedAt: nowIso(),
    actor: "Engineering",
  },
];

const seedRoomBlocks: RoomBlock[] = [
  {
    id: "RB-3180",
    roomId: "room-318",
    reason: "Owner block",
    from: "2026-03-22",
    until: "2026-03-25",
    status: "active",
    createdAt: nowIso(),
    updatedAt: nowIso(),
    actor: "Revenue Manager",
    note: "Hold for inspection visit.",
  },
];

const seedWorkOrders: WorkOrder[] = [
  {
    id: "WO-4012",
    title: "AC not cooling",
    roomId: "room-402",
    area: "Room 402",
    category: "HVAC",
    priority: "critical",
    status: "open",
    slaHours: SLA_BY_PRIORITY.critical,
    createdAt: nowIso(),
    dueAt: addHoursIso(3.5),
    assignedTo: "M. Novak",
  },
  {
    id: "WO-4015",
    title: "Bathroom leak",
    roomId: "room-318",
    area: "Room 318",
    category: "Plumbing",
    priority: "high",
    status: "assigned",
    slaHours: SLA_BY_PRIORITY.high,
    createdAt: nowIso(),
    dueAt: addHoursIso(18),
    assignedTo: "S. Roth",
  },
  {
    id: "WO-4018",
    title: "WiFi access point down",
    area: "Lobby",
    category: "IT",
    priority: "medium",
    status: "in_progress",
    slaHours: SLA_BY_PRIORITY.medium,
    createdAt: nowIso(),
    dueAt: addHoursIso(28),
    assignedTo: "J. Chen",
  },
  {
    id: "WO-4022",
    title: "Elevator panel check",
    area: "Building Core",
    category: "Electrical",
    priority: "low",
    status: "waiting_parts",
    slaHours: SLA_BY_PRIORITY.low,
    createdAt: nowIso(),
    dueAt: addHoursIso(56),
    assignedTo: "L. Adams",
  },
];

const seedGuests: Guest[] = [
  {
    id: "g-1001",
    firstName: "Anna",
    lastName: "Muller",
    dateOfBirth: "1989-04-12",
    nationality: "German",
    address: "Friedrichstrasse 80, 10117 Berlin, Germany",
    documentNumber: "C01X00A12",
    email: "anna.mueller@example.com",
    phone: "+49 151 123456",
    company: "ACME GmbH",
    segment: "Corporate",
    language: "de",
    vip: true,
    repeatGuest: true,
    preferences: { room: ["High floor"], service: ["Pillow: soft"], comms: ["Email"] },
  },
  {
    id: "g-1002",
    firstName: "Tomasz",
    lastName: "Kowalski",
    dateOfBirth: "",
    nationality: "",
    address: "",
    documentNumber: "",
    email: "t.kowalski@example.com",
    phone: "+48 600 111 222",
    segment: "Leisure",
    language: "pl",
  },
  {
    id: "g-1003",
    firstName: "Max",
    lastName: "Mueller",
    dateOfBirth: "1984-08-09",
    nationality: "German",
    address: "Talstrasse 14, 80331 Muenchen, Germany",
    documentNumber: "D98K12M44",
    email: "max.mueller@example.com",
    phone: "+49 171 555001",
    company: "Xentros Systems",
    segment: "Corporate",
    language: "de",
    repeatGuest: true,
  },
  {
    id: "g-1004",
    firstName: "Anna",
    lastName: "Becker",
    dateOfBirth: "1991-11-23",
    nationality: "German",
    address: "Lindenweg 5, 50968 Koeln, Germany",
    documentNumber: "B77C21Q10",
    email: "anna.becker@example.com",
    phone: "+49 152 555773",
    segment: "Leisure",
    language: "de",
  },
];

const seedReservations: Reservation[] = [
  {
    id: "R-1042",
    guestId: "g-1001",
    arrival: "2026-02-24",
    departure: "2026-02-26",
    adults: 2,
    children: 0,
    rooms: 1,
    ratePlanId: "bar",
    roomTypeId: "rt-dlx",
    roomId: "room-402",
    status: "booked",
    createdAt: nowIso(),
    source: "Direct",
    reservationType: "single",
    billingMode: "individual",
  },
  {
    id: "R-2140",
    guestId: "g-1003",
    arrival: "2026-03-19",
    departure: "2026-03-23",
    adults: 1,
    children: 0,
    rooms: 1,
    ratePlanId: "corp-flex",
    roomTypeId: "rt-std",
    roomId: "room-214",
    status: "inhouse",
    createdAt: nowIso(),
    source: "Direct",
    reservationType: "single",
    billingMode: "individual",
  },
  {
    id: "R-3070",
    guestId: "g-1004",
    arrival: "2026-03-18",
    departure: "2026-03-21",
    adults: 2,
    children: 0,
    rooms: 1,
    ratePlanId: "bar",
    roomTypeId: "rt-dlx",
    roomId: "room-307",
    status: "inhouse",
    createdAt: nowIso(),
    source: "OTA",
    reservationType: "single",
    billingMode: "individual",
  },
];

const seedStays: Stay[] = [
  {
    id: "ST-2140",
    reservationId: "R-2140",
    guestId: "g-1003",
    roomId: "room-214",
    arrival: "2026-03-19",
    departure: "2026-03-23",
    status: "inhouse",
  },
  {
    id: "ST-3070",
    reservationId: "R-3070",
    guestId: "g-1004",
    roomId: "room-307",
    arrival: "2026-03-18",
    departure: "2026-03-21",
    status: "inhouse",
  },
];

const seedChannelConnections: ChannelConnection[] = [
  {
    id: "conn-booking",
    providerCode: "booking_com",
    displayName: "Booking.com",
    providerType: "ota",
    status: "connected",
    enabled: true,
    supportsReservationImport: true,
    supportsRateSync: true,
    supportsAvailabilitySync: true,
  },
  {
    id: "conn-expedia",
    providerCode: "expedia",
    displayName: "Expedia",
    providerType: "ota",
    status: "degraded",
    enabled: true,
    supportsReservationImport: true,
    supportsRateSync: true,
    supportsAvailabilitySync: true,
    lastError: "Last availability sync was delayed.",
  },
];

const seedFolios: Folio[] = [
  {
    id: "F-2001",
    stayId: undefined,
    reservationId: "R-1042",
    guestId: "g-1001",
    currency: "EUR",
    balance: 0,
    totalCharges: 0,
    totalPayments: 0,
    totalRefunds: 0,
    status: "open",
  },
  {
    id: "F-2140",
    stayId: "ST-2140",
    reservationId: "R-2140",
    guestId: "g-1003",
    currency: "EUR",
    balance: 20.87,
    totalCharges: 20.87,
    totalPayments: 0,
    totalRefunds: 0,
    status: "open",
  },
  {
    id: "F-3070",
    stayId: "ST-3070",
    reservationId: "R-3070",
    guestId: "g-1004",
    currency: "EUR",
    balance: 0,
    totalCharges: 0,
    totalPayments: 0,
    totalRefunds: 0,
    status: "open",
  },
];

const seedGroupEvents: GroupEvent[] = [];
const seedIntegrations: IntegrationConnection[] = [
  {
    id: "int-pos",
    type: "pos",
    providerCode: "mock-pos",
    displayName: "Mock POS",
    status: "connected",
    enabled: true,
    retryLimit: 2,
    retryBackoffMs: 500,
    capabilities: { inbound: ["checks"], outbound: ["folio_postings"] },
  },
  {
    id: "int-pay",
    type: "payment_gateway",
    providerCode: "mock-pay",
    displayName: "Mock Payments",
    status: "connected",
    enabled: true,
    retryLimit: 2,
    retryBackoffMs: 500,
    capabilities: { inbound: ["payment_callbacks"], outbound: ["charges", "refunds"] },
  },
  {
    id: "int-key",
    type: "key_card",
    providerCode: "mock-key",
    displayName: "Mock Key Cards",
    status: "connected",
    enabled: true,
    retryLimit: 1,
    retryBackoffMs: 250,
    capabilities: { inbound: [], outbound: ["key_encoding"] },
  },
  {
    id: "int-channel",
    type: "channel_manager",
    providerCode: "mock-channel",
    displayName: "Mock Channel Manager",
    status: "degraded",
    enabled: true,
    retryLimit: 2,
    retryBackoffMs: 750,
    capabilities: { inbound: ["reservation_import"], outbound: ["rates", "availability"] },
  },
  {
    id: "int-rms",
    type: "revenue_management",
    providerCode: "mock-rms",
    displayName: "Mock RMS",
    status: "connected",
    enabled: true,
    retryLimit: 2,
    retryBackoffMs: 750,
    capabilities: { inbound: [], outbound: ["rate_recommendations"] },
  },
  {
    id: "int-hk",
    type: "housekeeping_app",
    providerCode: "mock-hk",
    displayName: "Mock Housekeeping App",
    status: "connected",
    enabled: true,
    retryLimit: 2,
    retryBackoffMs: 500,
    capabilities: { inbound: ["task_updates"], outbound: ["task_sync"] },
  },
  {
    id: "int-fiscal",
    type: "fiscal_printer",
    providerCode: "mock-fiscal",
    displayName: "Mock Fiscal Printer",
    status: "connected",
    enabled: true,
    retryLimit: 1,
    retryBackoffMs: 250,
    capabilities: { inbound: [], outbound: ["fiscal_receipts"] },
  },
  {
    id: "int-id",
    type: "id_scanner",
    providerCode: "mock-id",
    displayName: "Mock ID Scanner",
    status: "connected",
    enabled: true,
    retryLimit: 1,
    retryBackoffMs: 250,
    capabilities: { inbound: ["document_scans"], outbound: [] },
  },
];

const defaultStateBase: HosState = {
  guests: seedGuests,
  reservations: seedReservations,
  guestDocuments: [],
  frontDeskInvoices: [],
  frontDeskSettlements: [],
  frontDeskRoomMoves: [],
  frontDeskAudit: [],
  waitlistEntries: [],
  reservationAlerts: [],
  reservationAudit: [],
  overbookingSettings: {
    mode: "controlled",
    maxOverbookedRoomsPerRoomType: 1,
    alertThreshold: 1,
    autoPromoteWaitlist: true,
  },
  reservationHistory: [],
  groupEvents: seedGroupEvents,
  stays: seedStays,
  roomTypes: seedRoomTypes,
  rooms: seedRooms,
  channelConnections: seedChannelConnections,
  channelReservationLinks: [],
  channelSyncAttempts: [],
  housekeepingTasks: seedHousekeepingTasks,
  maintenanceReports: seedMaintenanceReports,
  roomBlocks: seedRoomBlocks,
  housekeepingAudit: [],
  housekeepingRealtimeFeed: [],
  workOrders: seedWorkOrders,
  folios: seedFolios,
  postings: [
    {
      id: "TX-2140",
      folioId: "F-2140",
      source: "pos",
      category: "restaurant",
      amount: 20.87,
      currency: "EUR",
      description: "POS BILL-3020 / Room Service / Room 214",
      postedAt: "2026-03-20T12:42:00.000Z",
      status: "posted",
    },
  ],
  payments: [],
  refunds: [],
  adjustments: [],
  billingInvoices: [],
  fiscalDocuments: [],
  billingAudit: [],
  integrations: seedIntegrations,
  integrationOperations: [],
  integrationLogs: [],
  keys: [],
  keycoderKeycards: [],
  keycoderOperationLogs: [],
  keycoderEncoderDevices: [],
  keycoderAccessCatalog: DEFAULT_ACCESS_CONTROL_CATALOG,
  parkingSessions: [],
  events: [],
};

const defaultState: HosState = {
  ...defaultStateBase,
  guests: syncGuestsWithOperationalState(defaultStateBase),
};

function buildGuestProfileContext(state: Pick<HosState, "guests" | "reservations" | "stays">) {
  return {
    guests: state.guests,
    reservations: state.reservations.map((reservation) => ({
      id: reservation.id,
      guestId: reservation.guestId,
      arrival: reservation.arrival,
      departure: reservation.departure,
      roomTypeId: reservation.roomTypeId,
      roomId: reservation.roomId,
      status: reservation.status,
      source: reservation.source,
      pricingSummary: reservation.pricingSummary
        ? { totalAmount: reservation.pricingSummary.totalAmount }
        : undefined,
    })),
    stays: state.stays,
    now: nowIso(),
  };
}

function syncGuestsWithOperationalState(state: HosState) {
  const context = buildGuestProfileContext(state);
  return state.guests.map((guest) => {
    const profile = syncGuestProfile(guest, context, { idFactory: id });
    const primaryEmail = profile.contactDetails.emails.find((entry) => entry.isPrimary)?.email ?? guest.email;
    const primaryPhone = profile.contactDetails.phones.find((entry) => entry.isPrimary)?.phone ?? guest.phone;
    const primaryAddress = profile.contactDetails.addresses.find((entry) => entry.isPrimary)?.addressLine ?? guest.address;

    return {
      ...guest,
      email: primaryEmail,
      phone: primaryPhone,
      address: primaryAddress,
      language: profile.contactDetails.preferredLanguage ?? guest.language,
      segment: profile.marketing.segmentLabels[0] ?? guest.segment,
      preferences: {
        room: profile.preferences
          .filter((entry) => entry.isActive && entry.scope === "global" && (entry.category === "room_type" || entry.category === "room_location" || entry.category === "pillow" || entry.category === "bed"))
          .map((entry) => entry.label),
        service: profile.preferences
          .filter((entry) => entry.isActive && entry.scope === "global" && entry.category !== "communication" && entry.category !== "room_type" && entry.category !== "room_location" && entry.category !== "pillow" && entry.category !== "bed")
          .map((entry) => entry.label),
        comms: profile.preferences
          .filter((entry) => entry.isActive && entry.scope === "global" && entry.category === "communication")
          .map((entry) => entry.label),
      },
      crmProfile: profile,
      repeatGuest: guest.repeatGuest ?? profile.stayHistory.filter((entry) => entry.status === "checkedout" || entry.status === "inhouse").length > 1,
      vip: guest.vip ?? (profile.loyalty?.tier === "gold" || profile.loyalty?.tier === "platinum"),
    };
  });
}

function loadInitialState(): HosState {
  return defaultState;
}

function getInventoryByRoomType(state: Pick<HosState, "roomTypes" | "rooms">) {
  return state.roomTypes.reduce<Record<string, number>>((acc, roomType) => {
    acc[roomType.id] = state.rooms.filter((room) => room.typeId === roomType.id && room.availabilityStatus === "available").length;
    return acc;
  }, {});
}

function buildReservationManagementContext(state: HosState) {
  return {
    reservations: state.reservations.map((reservation) => ({
      id: reservation.id,
      guestId: reservation.guestId,
      arrival: reservation.arrival,
      departure: reservation.departure,
      roomTypeId: reservation.roomTypeId,
      rooms: reservation.rooms,
      status: reservation.status,
      createdAt: reservation.createdAt,
      source: reservation.source,
      pricingSnapshot: reservation.pricingSummary
        ? {
            nightlyRate: reservation.pricingSummary.nightlyRate,
            totalAmount: reservation.pricingSummary.totalAmount,
            taxAmount: reservation.pricingSummary.taxAmount,
            currency: reservation.pricingSummary.currency,
          }
        : undefined,
      deposit: reservation.deposit,
      confirmation: reservation.confirmation,
      overbookingDecision: reservation.overbookingDecision,
    })),
    waitlistEntries: state.waitlistEntries,
    settings: state.overbookingSettings,
    inventoryByRoomType: getInventoryByRoomType(state),
    now: nowIso(),
  };
}

function mergeReservationAlerts(current: ReservationAlert[], additions: ReservationAlert[]) {
  const existingIds = new Set(current.map((alert) => alert.id));
  return [...additions.filter((alert) => !existingIds.has(alert.id)), ...current];
}

function mergeReservationAudit(current: ReservationAuditEntry[], additions: ReservationAuditEntry[]) {
  const existingIds = new Set(current.map((entry) => entry.id));
  return [...additions.filter((entry) => !existingIds.has(entry.id)), ...current];
}

function buildReservationFolio(reservation: Pick<Reservation, "id" | "guestId">): Folio {
  return {
    id: id("F"),
    reservationId: reservation.id,
    guestId: reservation.guestId,
    folioType: "reservation",
    currency: "EUR",
    balance: 0,
    totalCharges: 0,
    totalPayments: 0,
    totalRefunds: 0,
    status: "open",
  };
}

function buildReservationFolioAuditEntry(folio: Folio, actor?: string): BillingAuditEntry {
  return {
    id: id("bill-audit"),
    folioId: folio.id,
    timestamp: nowIso(),
    action: "folio_created",
    actor,
    message: `Reservation folio ${folio.id} created for ${folio.reservationId ?? "reservation"}.`,
    metadata: folio.reservationId ? { reservationId: folio.reservationId } : undefined,
  };
}

function buildFrontDeskContext(state: HosState) {
  return {
    reservations: state.reservations.map((reservation) => ({
      id: reservation.id,
      guestId: reservation.guestId,
      arrival: reservation.arrival,
      departure: reservation.departure,
      roomId: reservation.roomId,
      roomTypeId: reservation.roomTypeId,
      status: reservation.status,
    })),
    stays: state.stays.map((stay) => ({
      id: stay.id,
      reservationId: stay.reservationId,
      guestId: stay.guestId,
      roomId: stay.roomId,
      arrival: stay.arrival,
      departure: stay.departure,
      status: stay.status,
    })),
    rooms: state.rooms.map((room) => ({
      id: room.id,
      number: room.number,
      typeId: room.typeId,
      status: room.status,
      housekeepingStatus: room.housekeepingStatus,
      availabilityStatus: room.availabilityStatus,
    })),
    guests: state.guests.map((guest) => ({
      id: guest.id,
      firstName: guest.firstName,
      lastName: guest.lastName,
      dateOfBirth: guest.dateOfBirth,
      nationality: guest.nationality,
      address: guest.address,
      documentNumber: guest.documentNumber,
      email: guest.email,
    })),
    folios: state.folios.map((folio) => ({
      folioId: folio.id,
      reservationId: folio.reservationId,
      stayId: folio.stayId,
      guestId: folio.guestId,
      balance: folio.balance,
      status: folio.status,
    })),
    documents: state.guestDocuments,
    now: nowIso(),
  };
}

function buildHousekeepingContext(state: HosState) {
  return {
    rooms: state.rooms,
    tasks: state.housekeepingTasks,
    maintenanceReports: state.maintenanceReports,
    roomBlocks: state.roomBlocks,
    now: nowIso(),
  };
}

function buildBillingContext(state: HosState) {
  return {
    folios: state.folios,
    items: state.postings,
    payments: state.payments,
    refunds: state.refunds,
    adjustments: state.adjustments,
    invoices: state.billingInvoices,
    fiscalDocuments: state.fiscalDocuments,
    now: nowIso(),
  };
}

function buildChannelManagerContext(state: HosState) {
  return {
    connections: state.channelConnections,
    syncedReservations: state.channelReservationLinks,
    reservations: state.reservations,
    roomTypes: state.roomTypes,
    inventoryByRoomType: getInventoryByRoomType(state),
    now: nowIso(),
    overbookingSettings: state.overbookingSettings,
  };
}

function buildIntegrationContext(state: HosState) {
  return {
    connections: state.integrations,
    operationHistory: state.integrationOperations,
    logs: state.integrationLogs,
    now: nowIso(),
  };
}

export function HosProvider({ children }: { children: React.ReactNode }) {
  const [storedState, setState] = useState<HosState>(() => loadInitialState());
  const state = useMemo<HosState>(
    () => ({
      ...storedState,
      guests: syncGuestsWithOperationalState(storedState),
    }),
    [storedState]
  );

  const eventsRef = useRef<HosEvent[]>([]);

  const emit = (type: HosEventType, payload?: Record<string, unknown>) => {
    const event: HosEvent = { id: id("evt"), type, ts: nowIso(), payload };
    eventsRef.current = [...eventsRef.current, event];
    setState((prev) => ({ ...prev, events: [...prev.events, event] }));
  };

  const actions: HosActions = useMemo(
    () => ({
      createRoomType: (input) => {
        const nextRoomType: RoomType = {
          id: id("rt"),
          name: input.name.trim(),
          shortCode: input.shortCode?.trim() || undefined,
          description: input.description?.trim() || undefined,
          capacity: input.capacity,
          availability: input.availability,
        };
        setState((prev) => ({
          ...prev,
          roomTypes: [nextRoomType, ...prev.roomTypes],
        }));
        return nextRoomType;
      },
      updateRoomType: (roomTypeId, input) => {
        let result: RoomType | null = null;
        setState((prev) => {
          const existing = prev.roomTypes.find((roomType) => roomType.id === roomTypeId);
          if (!existing) return prev;
          result = {
            ...existing,
            ...input,
            name: input.name?.trim() ?? existing.name,
            shortCode: input.shortCode === undefined ? existing.shortCode : input.shortCode.trim() || undefined,
            description: input.description === undefined ? existing.description : input.description.trim() || undefined,
            capacity: input.capacity ?? existing.capacity,
            availability: input.availability ?? existing.availability,
          };
          return {
            ...prev,
            roomTypes: prev.roomTypes.map((roomType) => (roomType.id === roomTypeId ? result ?? roomType : roomType)),
          };
        });
        return result;
      },
      upsertGuest: (input) => {
        const normalizedMandatoryFields = normalizeMandatoryGuestInfo(input);
        let result: Guest = {
          id: id("g"),
          language: "de",
          dateOfBirth: "",
          nationality: "",
          address: "",
          documentNumber: "",
          ...normalizedMandatoryFields,
        };
        setState((prev) => {
          const existing = prev.guests.find((g) => g.email.toLowerCase() === input.email.toLowerCase());
          if (existing) {
            const nextState = {
              ...prev,
              guests: prev.guests.map((g) =>
                g.id === existing.id ? { ...g, ...normalizedMandatoryFields } : g
              ),
            };
            const syncedGuests = syncGuestsWithOperationalState(nextState);
            result = syncedGuests.find((guest) => guest.id === existing.id) ?? { ...existing, ...normalizedMandatoryFields };
            return { ...nextState, guests: syncedGuests };
          }
          const nextState = {
            ...prev,
            guests: [result, ...prev.guests],
          };
          const syncedGuests = syncGuestsWithOperationalState(nextState);
          result = syncedGuests.find((guest) => guest.id === result.id) ?? result;
          return { ...nextState, guests: syncedGuests };
        });
        return result;
      },
      updateGuest: (guestId, input) => {
        const normalizedInput = normalizeMandatoryGuestInfo(input);
        let result: Guest | null = null;
        setState((prev) => {
          const existing = prev.guests.find((guest) => guest.id === guestId);
          if (!existing) return prev;
          const nextState = {
            ...prev,
            guests: prev.guests.map((guest) => (guest.id === guestId ? { ...existing, ...normalizedInput } : guest)),
          };
          const syncedGuests = syncGuestsWithOperationalState(nextState);
          result = syncedGuests.find((guest) => guest.id === guestId) ?? null;
          return {
            ...nextState,
            guests: syncedGuests,
          };
        });
        return result;
      },
      addGuestNote: (input) => {
        const title = input.title.trim();
        const body = input.body.trim();
        if (!title || !body) return null;

        const nextNote = createGuestNote(input);
        let result: GuestNote | null = nextNote;

        setState((prev) => {
          const existing = prev.guests.find((guest) => guest.id === input.guestId);
          if (!existing) {
            result = null;
            return prev;
          }

          const nextState = {
            ...prev,
            guests: prev.guests.map((guest) =>
              guest.id === input.guestId
                ? {
                    ...guest,
                    crmProfile: {
                      ...(guest.crmProfile ?? syncGuestProfile(guest, buildGuestProfileContext(prev), { idFactory: id })),
                      notes: [nextNote, ...(guest.crmProfile?.notes ?? [])],
                    },
                  }
                : guest
            ),
          };
          const syncedGuests = syncGuestsWithOperationalState(nextState);
          result = syncedGuests.find((guest) => guest.id === input.guestId)?.crmProfile?.notes.find((note) => note.id === nextNote.id) ?? nextNote;
          return { ...nextState, guests: syncedGuests };
        });

        return result;
      },
      updateGuestContactDetails: (input) => {
        const result = guestProfileManagementEndpoints.updateContactDetails(
          input,
          buildGuestProfileContext(state),
          { idFactory: id }
        );
        if (!result.ok) return result;

        let guestResult: Guest | undefined;
        setState((prev) => {
          const nextState = {
            ...prev,
            guests: prev.guests.map((guest) =>
              guest.id === input.guestId ? { ...guest, crmProfile: result.profile } : guest
            ),
          };
          const syncedGuests = syncGuestsWithOperationalState(nextState);
          guestResult = syncedGuests.find((guest) => guest.id === input.guestId);
          return { ...nextState, guests: syncedGuests };
        });

        return { ...result, guest: guestResult };
      },
      upsertGuestPreference: (input) => {
        const result = guestProfileManagementEndpoints.upsertPreference(
          input,
          buildGuestProfileContext(state),
          { idFactory: id }
        );
        if (!result.ok) return result;

        let guestResult: Guest | undefined;
        setState((prev) => {
          const nextState = {
            ...prev,
            guests: prev.guests.map((guest) =>
              guest.id === input.guestId
                ? { ...guest, crmProfile: result.profile, preferences: result.legacyPreferences }
                : guest
            ),
          };
          const syncedGuests = syncGuestsWithOperationalState(nextState);
          guestResult = syncedGuests.find((guest) => guest.id === input.guestId);
          return { ...nextState, guests: syncedGuests };
        });

        return { ...result, guest: guestResult };
      },
      enrollGuestLoyalty: (input) => {
        const result = guestProfileManagementEndpoints.enrollLoyalty(
          input,
          buildGuestProfileContext(state),
          { idFactory: id }
        );
        if (!result.ok) return result;

        let guestResult: Guest | undefined;
        setState((prev) => {
          const nextState = {
            ...prev,
            guests: prev.guests.map((guest) =>
              guest.id === input.guestId
                ? { ...guest, crmProfile: result.profile, vip: result.profile.loyalty?.tier === "gold" || result.profile.loyalty?.tier === "platinum" }
                : guest
            ),
          };
          const syncedGuests = syncGuestsWithOperationalState(nextState);
          guestResult = syncedGuests.find((guest) => guest.id === input.guestId);
          return { ...nextState, guests: syncedGuests };
        });

        return { ...result, guest: guestResult };
      },
      updateGuestMarketing: (input) => {
        const result = guestProfileManagementEndpoints.updateMarketing(
          input,
          buildGuestProfileContext(state),
          { idFactory: id }
        );
        if (!result.ok) return result;

        let guestResult: Guest | undefined;
        setState((prev) => {
          const nextState = {
            ...prev,
            guests: prev.guests.map((guest) =>
              guest.id === input.guestId
                ? { ...guest, crmProfile: result.profile, segment: result.profile.marketing.segmentLabels[0] ?? guest.segment }
                : guest
            ),
          };
          const syncedGuests = syncGuestsWithOperationalState(nextState);
          guestResult = syncedGuests.find((guest) => guest.id === input.guestId);
          return { ...nextState, guests: syncedGuests };
        });

        return { ...result, guest: guestResult };
      },
      mergeGuestProfiles: (input) => {
        const result = guestProfileManagementEndpoints.mergeProfiles(
          input,
          buildGuestProfileContext(state),
          { idFactory: id }
        );
        if (!result.ok) return result;

        let targetGuest: Guest | undefined;
        let sourceGuest: Guest | undefined;
        setState((prev) => {
          const nextState = {
            ...prev,
            guests: prev.guests.map((guest) => {
              if (guest.id === input.targetGuestId) {
                return {
                  ...guest,
                  crmProfile: result.mergedProfile,
                };
              }
              if (guest.id === input.sourceGuestId) {
                return {
                  ...guest,
                  crmProfile: result.sourceProfile,
                  flagged: true,
                };
              }
              return guest;
            }),
            reservations: prev.reservations.map((reservation) =>
              reservation.guestId === input.sourceGuestId
                ? { ...reservation, guestId: input.targetGuestId, primaryGuestId: reservation.primaryGuestId === input.sourceGuestId ? input.targetGuestId : reservation.primaryGuestId }
                : reservation
            ),
            stays: prev.stays.map((stay) =>
              stay.guestId === input.sourceGuestId ? { ...stay, guestId: input.targetGuestId } : stay
            ),
            folios: prev.folios.map((folio) =>
              folio.guestId === input.sourceGuestId ? { ...folio, guestId: input.targetGuestId } : folio
            ),
            guestDocuments: prev.guestDocuments.map((document) =>
              document.guestId === input.sourceGuestId ? { ...document, guestId: input.targetGuestId } : document
            ),
            keys: prev.keys.map((key) =>
              key.guestId === input.sourceGuestId ? { ...key, guestId: input.targetGuestId } : key
            ),
            parkingSessions: prev.parkingSessions.map((session) =>
              session.guestId === input.sourceGuestId ? { ...session, guestId: input.targetGuestId } : session
            ),
          };
          const syncedGuests = syncGuestsWithOperationalState(nextState);
          targetGuest = syncedGuests.find((guest) => guest.id === input.targetGuestId);
          sourceGuest = syncedGuests.find((guest) => guest.id === input.sourceGuestId);
          return { ...nextState, guests: syncedGuests };
        });

        return { ...result, targetGuest, sourceGuest };
      },
      runIntegrationHealthCheck: async (connectionId) => {
        const result = await integrationEndpoints.healthCheck(
          connectionId,
          buildIntegrationContext(state),
          integrationAdapters,
          { idFactory: id }
        );
        if (!result.ok) return result;

        setState((prev) => ({
          ...prev,
          integrations: prev.integrations.map((connection) => (connection.id === connectionId ? result.connection ?? connection : connection)),
          integrationLogs: [...(result.logs ?? []), ...prev.integrationLogs],
        }));

        return result;
      },
      runIntegrationMockOperation: async (connectionId) => {
        const connection = state.integrations.find((entry) => entry.id === connectionId);
        if (!connection) return { ok: false, message: "Integration connection not found." };

        const operationNameByType: Record<IntegrationConnection["type"], string> = {
          pos: "post_charge",
          payment_gateway: "capture_payment",
          key_card: "encode_key",
          channel_manager: "sync_availability",
          revenue_management: "rate_recommendation",
          housekeeping_app: "sync_task",
          fiscal_printer: "print_receipt",
          id_scanner: "scan_document",
        };

        const payloadByType: Record<IntegrationConnection["type"], Record<string, unknown>> = {
          pos: { reservationId: "R-2140", stayId: "ST-2140", roomId: "room-214", guestId: "g-1003", amount: 24.5, currency: "EUR", outlet: "Lobby Bar", description: "Signature cocktail" },
          payment_gateway: { folioId: "F-2140", amount: 24.5, currency: "EUR", reference: "folio-topup" },
          key_card: { stayId: "ST-2140", reservationId: "R-2140", roomId: "room-214", guestId: "g-1003" },
          channel_manager: { connectionId: "conn-booking", idempotencyKey: `sync-${Date.now()}` },
          revenue_management: { from: new Date().toISOString().slice(0, 10), to: new Date(Date.now() + 86400000).toISOString().slice(0, 10), occupancy: 68, adr: 138, roomTypeId: "rt-std" },
          housekeeping_app: { roomId: "room-214", taskId: "HK-2140", taskType: "cleaning", status: "assigned" },
          fiscal_printer: { invoiceId: "INV-1001", folioId: "F-2140", totalAmount: 24.5, currency: "EUR" },
          id_scanner: { guestId: "g-1003", reservationId: "R-2140" },
        };

        const result = await integrationEndpoints.execute(
          {
            connectionId,
            operation: operationNameByType[connection.type],
            payload: payloadByType[connection.type],
            idempotencyKey: `${connectionId}-${Date.now()}`,
          },
          buildIntegrationContext(state),
          integrationAdapters,
          {
            idFactory: id,
            dispatcher: {
              pos: (adapter, payload) => adapter.postCharge(payload as never),
              payment_gateway: (adapter, payload) => adapter.charge(payload as never),
              key_card: (adapter, payload) => adapter.encodeKey(payload as never),
              channel_manager: (adapter, payload) => adapter.syncAvailability(payload as never),
              revenue_management: (adapter, payload) => adapter.recommendRate(payload as never),
              housekeeping_app: (adapter, payload) => adapter.syncTask(payload as never),
              fiscal_printer: (adapter, payload) => adapter.printFiscalReceipt(payload as never),
              id_scanner: (adapter, payload) => adapter.scanDocument(payload as never),
            },
          }
        );

        setState((prev) => ({
          ...prev,
          integrations: result.connection
            ? prev.integrations.map((entry) => (entry.id === connectionId ? result.connection ?? entry : entry))
            : prev.integrations,
          integrationOperations: result.operationRecord ? [result.operationRecord, ...prev.integrationOperations] : prev.integrationOperations,
          integrationLogs: [...(result.logs ?? []), ...prev.integrationLogs],
        }));

        return {
          ok: result.ok,
          message: result.message,
          connection: result.connection,
          operation: result.operationRecord,
          logs: result.logs,
        };
      },
      createReservation: (input) => {
        const newRes: Reservation = {
          ...input,
          id: id("R"),
          status: "booked",
          createdAt: nowIso(),
          reservationType: input.reservationType ?? "single",
          billingMode: input.billingMode ?? "individual",
        };
        const folio = buildReservationFolio(newRes);
        setState((prev) => ({
          ...prev,
          reservations: [newRes, ...prev.reservations],
          folios: [folio, ...prev.folios],
          billingAudit: [buildReservationFolioAuditEntry(folio), ...prev.billingAudit],
        }));
        emit("ReservationCreated", { reservationId: newRes.id });
        return newRes;
      },
      createManagedReservation: async (input) => {
        const result = reservationManagementEndpoints.createReservation(
          input,
          buildReservationManagementContext(state),
          { idFactory: id }
        );

        if (!result.ok) return result;

        const newReservation: Reservation = {
          id: result.reservation.id,
          guestId: result.reservation.guestId,
          arrival: result.reservation.arrival,
          departure: result.reservation.departure,
          adults: 1,
          children: 0,
          rooms: result.reservation.rooms,
          ratePlanId: "bar",
          roomTypeId: result.reservation.roomTypeId,
          status: result.reservation.status,
          createdAt: result.reservation.createdAt,
          source: result.reservation.source,
          reservationType: "single",
          billingMode: "individual",
          pricingSummary: {
            nightlyRate: result.reservation.pricingSnapshot?.nightlyRate ?? 0,
            totalAmount: result.reservation.pricingSnapshot?.totalAmount ?? 0,
            taxAmount: result.reservation.pricingSnapshot?.taxAmount ?? 0,
            currency: result.reservation.pricingSnapshot?.currency ?? "EUR",
          },
          deposit: result.deposit as ReservationDeposit,
          confirmation: result.confirmation as ReservationConfirmation,
          overbookingDecision: result.overbookingDecision,
        };
        const folio = buildReservationFolio(newReservation);

        setState((prev) => ({
          ...prev,
          reservations: [newReservation, ...prev.reservations],
          folios: [folio, ...prev.folios],
          billingAudit: [buildReservationFolioAuditEntry(folio, input.actor), ...prev.billingAudit],
          reservationAlerts: mergeReservationAlerts(prev.reservationAlerts, result.alerts),
          reservationAudit: mergeReservationAudit(prev.reservationAudit, result.auditEntries),
        }));

        emit("ReservationCreated", { reservationId: newReservation.id });

        if (input.sendConfirmationEmail && input.guestEmail) {
          const confirmationResult = await reservationManagementEndpoints.sendConfirmation(
            {
              ...result.reservation,
              pricingSnapshot: result.reservation.pricingSnapshot,
              deposit: result.deposit,
              confirmation: result.confirmation,
              overbookingDecision: result.overbookingDecision,
            },
            {
              reservationId: result.reservation.id,
              recipientEmail: input.guestEmail,
              actor: input.actor,
            },
            reservationConfirmationProvider,
            nowIso(),
            {
              idFactory: id,
              guestLabel: input.guestLabel,
            }
          );

          setState((prev) => ({
            ...prev,
            reservations: prev.reservations.map((reservation) =>
              reservation.id === result.reservation.id
                ? {
                    ...reservation,
                    status: confirmationResult.ok ? "booked" : reservation.status,
                    confirmation: confirmationResult.confirmation as ReservationConfirmation,
                  }
                : reservation
            ),
            reservationAudit: mergeReservationAudit(prev.reservationAudit, [confirmationResult.auditEntry]),
            reservationAlerts: !confirmationResult.ok
              ? mergeReservationAlerts(prev.reservationAlerts, [
                  {
                    id: id("alert"),
                    type: "confirmation_failed",
                    severity: "warning",
                    createdAt: nowIso(),
                    reservationId: result.reservation.id,
                    message: confirmationResult.message,
                  },
                ])
              : prev.reservationAlerts,
          }));

          if (confirmationResult.ok) emit("ConfirmationSent", { reservationId: result.reservation.id });
        }

        return result;
      },
      recordReservationDeposit: (input) => {
        const reservation = state.reservations.find((entry) => entry.id === input.reservationId);
        if (!reservation) {
          return { ok: false, message: "Reservation not found." };
        }

        const result = reservationManagementEndpoints.recordDeposit(
          {
            id: reservation.id,
            guestId: reservation.guestId,
            arrival: reservation.arrival,
            departure: reservation.departure,
            roomTypeId: reservation.roomTypeId,
            rooms: reservation.rooms,
            status: reservation.status,
            createdAt: reservation.createdAt,
            source: reservation.source,
            pricingSnapshot: reservation.pricingSummary
              ? {
                  nightlyRate: reservation.pricingSummary.nightlyRate,
                  totalAmount: reservation.pricingSummary.totalAmount,
                  taxAmount: reservation.pricingSummary.taxAmount,
                  currency: reservation.pricingSummary.currency,
                }
              : undefined,
            deposit: reservation.deposit,
            confirmation: reservation.confirmation,
            overbookingDecision: reservation.overbookingDecision,
          },
          input,
          nowIso(),
          { idFactory: id }
        );

        if (!result.ok) return result;

        setState((prev) => ({
          ...prev,
          reservations: prev.reservations.map((entry) =>
            entry.id === input.reservationId ? { ...entry, deposit: result.deposit, status: entry.status === "pending_confirmation" ? "booked" : entry.status } : entry
          ),
          reservationAudit: mergeReservationAudit(prev.reservationAudit, [result.auditEntry]),
          reservationAlerts: mergeReservationAlerts(prev.reservationAlerts, result.alerts),
        }));
        emit("DepositRecorded", { reservationId: input.reservationId });
        return result;
      },
      sendReservationConfirmation: async (input) => {
        const reservation = state.reservations.find((entry) => entry.id === input.reservationId);
        if (!reservation) {
          return {
            ok: false,
            message: "Reservation not found.",
            confirmation: {
              status: "failed",
              recipientEmail: input.recipientEmail,
              lastError: "Reservation not found.",
            },
            auditEntry: {
              id: id("audit"),
              reservationId: input.reservationId,
              timestamp: nowIso(),
              action: "confirmation_failed",
              actor: input.actor,
              message: "Reservation not found.",
            },
          };
        }

        const guest = state.guests.find((entry) => entry.id === reservation.guestId);
        const result = await reservationManagementEndpoints.sendConfirmation(
          {
            id: reservation.id,
            guestId: reservation.guestId,
            arrival: reservation.arrival,
            departure: reservation.departure,
            roomTypeId: reservation.roomTypeId,
            rooms: reservation.rooms,
            status: reservation.status,
            createdAt: reservation.createdAt,
            source: reservation.source,
            pricingSnapshot: reservation.pricingSummary
              ? {
                  nightlyRate: reservation.pricingSummary.nightlyRate,
                  totalAmount: reservation.pricingSummary.totalAmount,
                  taxAmount: reservation.pricingSummary.taxAmount,
                  currency: reservation.pricingSummary.currency,
                }
              : undefined,
            deposit: reservation.deposit,
            confirmation: reservation.confirmation,
            overbookingDecision: reservation.overbookingDecision,
          },
          input,
          reservationConfirmationProvider,
          nowIso(),
          {
            idFactory: id,
            guestLabel: guest ? `${guest.firstName} ${guest.lastName}`.trim() : "Guest",
          }
        );

        setState((prev) => ({
          ...prev,
          reservations: prev.reservations.map((entry) =>
            entry.id === input.reservationId
              ? {
                  ...entry,
                  status: result.ok && entry.status === "pending_confirmation" ? "booked" : entry.status,
                  confirmation: result.confirmation as ReservationConfirmation,
                }
              : entry
          ),
          reservationAudit: mergeReservationAudit(prev.reservationAudit, [result.auditEntry]),
          reservationAlerts: !result.ok
            ? mergeReservationAlerts(prev.reservationAlerts, [
                {
                  id: id("alert"),
                  type: "confirmation_failed",
                  severity: "warning",
                  createdAt: nowIso(),
                  reservationId: input.reservationId,
                  message: result.message,
                },
              ])
            : prev.reservationAlerts,
        }));

        if (result.ok) emit("ConfirmationSent", { reservationId: input.reservationId });
        return result;
      },
      createWaitlistEntry: (input) => {
        const result = reservationManagementEndpoints.createWaitlistEntry(input, nowIso(), { idFactory: id });
        if (!result.ok) return result;
        setState((prev) => ({
          ...prev,
          waitlistEntries: [result.entry, ...prev.waitlistEntries],
          reservationAlerts: mergeReservationAlerts(prev.reservationAlerts, [result.alert]),
          reservationAudit: mergeReservationAudit(prev.reservationAudit, [result.auditEntry]),
        }));
        emit("ReservationWaitlisted", { waitlistEntryId: result.entry.id, roomTypeId: result.entry.roomTypeId });
        return result;
      },
      promoteWaitlistEntry: async (input) => {
        const waitlistEntry = state.waitlistEntries.find((entry) => entry.id === input.waitlistEntryId && entry.status === "active");
        if (!waitlistEntry) {
          return { ok: false, message: "Waitlist entry not found." };
        }

        const result = reservationManagementEndpoints.promoteWaitlistEntry(
          input,
          {
            ...buildReservationManagementContext(state),
            waitlistEntry,
          },
          { idFactory: id }
        );
        if (!result.ok) return result;

        const newReservation: Reservation = {
          id: result.reservation.id,
          guestId: result.reservation.guestId,
          arrival: result.reservation.arrival,
          departure: result.reservation.departure,
          adults: 1,
          children: 0,
          rooms: result.reservation.rooms,
          ratePlanId: "bar",
          roomTypeId: result.reservation.roomTypeId,
          status: result.reservation.status,
          createdAt: result.reservation.createdAt,
          source: result.reservation.source,
          reservationType: "single",
          billingMode: "individual",
          pricingSummary: {
            nightlyRate: result.reservation.pricingSnapshot?.nightlyRate ?? 0,
            totalAmount: result.reservation.pricingSnapshot?.totalAmount ?? 0,
            taxAmount: result.reservation.pricingSnapshot?.taxAmount ?? 0,
            currency: result.reservation.pricingSnapshot?.currency ?? "EUR",
          },
          deposit: result.reservation.deposit,
          confirmation: result.reservation.confirmation,
          overbookingDecision: result.reservation.overbookingDecision,
        };
        const folio = buildReservationFolio(newReservation);

        setState((prev) => ({
          ...prev,
          reservations: [newReservation, ...prev.reservations],
          waitlistEntries: prev.waitlistEntries.map((entry) => (entry.id === result.waitlistEntry.id ? result.waitlistEntry : entry)),
          folios: [folio, ...prev.folios],
          billingAudit: [buildReservationFolioAuditEntry(folio, input.actor), ...prev.billingAudit],
          reservationAlerts: mergeReservationAlerts(prev.reservationAlerts, result.alerts),
          reservationAudit: mergeReservationAudit(prev.reservationAudit, result.auditEntries),
        }));

        emit("WaitlistPromoted", { waitlistEntryId: result.waitlistEntry.id, reservationId: newReservation.id });
        emit("ReservationCreated", { reservationId: newReservation.id });

        if (input.sendConfirmationEmail && waitlistEntry.email) {
          const confirmationResult = await reservationManagementEndpoints.sendConfirmation(
            {
              id: newReservation.id,
              guestId: newReservation.guestId,
              arrival: newReservation.arrival,
              departure: newReservation.departure,
              roomTypeId: newReservation.roomTypeId,
              rooms: newReservation.rooms,
              status: newReservation.status,
              createdAt: newReservation.createdAt,
              source: newReservation.source,
              pricingSnapshot: newReservation.pricingSummary
                ? {
                    nightlyRate: newReservation.pricingSummary.nightlyRate,
                    totalAmount: newReservation.pricingSummary.totalAmount,
                    taxAmount: newReservation.pricingSummary.taxAmount,
                    currency: newReservation.pricingSummary.currency,
                  }
                : undefined,
              deposit: newReservation.deposit,
              confirmation: newReservation.confirmation,
              overbookingDecision: newReservation.overbookingDecision,
            },
            {
              reservationId: newReservation.id,
              recipientEmail: waitlistEntry.email,
              actor: input.actor,
            },
            reservationConfirmationProvider,
            nowIso(),
            {
              idFactory: id,
              guestLabel: waitlistEntry.guestLabel,
            }
          );

          setState((prev) => ({
            ...prev,
            reservations: prev.reservations.map((entry) =>
              entry.id === newReservation.id
                ? {
                    ...entry,
                    status: confirmationResult.ok ? "booked" : entry.status,
                    confirmation: confirmationResult.confirmation as ReservationConfirmation,
                  }
                : entry
            ),
            reservationAudit: mergeReservationAudit(prev.reservationAudit, [confirmationResult.auditEntry]),
            reservationAlerts: !confirmationResult.ok
              ? mergeReservationAlerts(prev.reservationAlerts, [
                  {
                    id: id("alert"),
                    type: "confirmation_failed",
                    severity: "warning",
                    createdAt: nowIso(),
                    reservationId: newReservation.id,
                    message: confirmationResult.message,
                  },
                ])
              : prev.reservationAlerts,
          }));

          if (confirmationResult.ok) emit("ConfirmationSent", { reservationId: newReservation.id });
        }

        return result;
      },
      acknowledgeReservationAlert: (alertId) => {
        setState((prev) => ({
          ...prev,
          reservationAlerts: prev.reservationAlerts.map((alert) =>
            alert.id === alertId ? { ...alert, acknowledgedAt: nowIso() } : alert
          ),
        }));
      },
      updateOverbookingSettings: (input) => {
        setState((prev) => ({
          ...prev,
          overbookingSettings: {
            ...prev.overbookingSettings,
            ...input,
          },
        }));
      },
      registerGuestDocument: (input) => {
        const result = frontDeskEndpoints.registerGuestDocument(input, nowIso(), { idFactory: id });
        if (!result.ok) return result;
        setState((prev) => ({
          ...prev,
          guestDocuments: [result.document, ...prev.guestDocuments],
          frontDeskAudit: [result.auditEntry, ...prev.frontDeskAudit],
          guests: prev.guests.map((guest) =>
            guest.id === input.guestId
              ? {
                  ...guest,
                  nationality: input.nationality,
                  documentNumber: input.documentNumber,
                }
              : guest
          ),
        }));
        emit("DocumentRegistered", { guestId: input.guestId, reservationId: input.reservationId });
        return result;
      },
      executeFrontDeskCheckin: async (input) => {
        const result = await frontDeskEndpoints.executeCheckin(input, buildFrontDeskContext(state), {
          idFactory: id,
          keycardProvider: frontDeskKeycardProvider,
        });
        if (!result.ok) return result;

        const reservation = state.reservations.find((entry) => entry.id === input.reservationId);
        if (!reservation) return { ok: false, message: "Reservation not found." };
        const stay: Stay = {
          id: result.stay.id,
          reservationId: result.stay.reservationId,
          guestId: result.stay.guestId,
          roomId: result.stay.roomId,
          arrival: result.stay.arrival,
          departure: result.stay.departure,
          status: "inhouse",
        };
        const key = result.keycard
          ? {
              id: result.keycard.id,
              stayId: result.keycard.stayId,
              guestId: result.keycard.guestId,
              roomId: result.keycard.roomId,
              status: "active" as const,
              validFrom: stay.arrival,
              validTo: stay.departure,
            }
          : null;

        setState((prev) => ({
          ...prev,
          reservations: prev.reservations.map((entry) =>
            entry.id === input.reservationId ? { ...entry, status: "inhouse", roomId: input.roomId } : entry
          ),
          stays: [stay, ...prev.stays.filter((entry) => entry.reservationId !== input.reservationId)],
          folios: prev.folios.map((folio) =>
            folio.reservationId === input.reservationId ? { ...folio, stayId: stay.id } : folio
          ),
          keys: key ? [key, ...prev.keys.filter((entry) => entry.stayId !== stay.id)] : prev.keys,
          frontDeskAudit: [...(result.auditEntries ?? []), ...prev.frontDeskAudit],
        }));

        emit("CheckInCompleted", { reservationId: input.reservationId, stayId: stay.id });
        if (key) emit("KeyIssued", { reservationId: input.reservationId, stayId: stay.id, keyId: key.id });
        return { ok: true, stay, roomId: input.roomId, key, auditEntries: result.auditEntries };
      },
      moveStayRoom: async (input) => {
        const result = await frontDeskEndpoints.moveStayRoom(input, buildFrontDeskContext(state), {
          idFactory: id,
          keycardProvider: frontDeskKeycardProvider,
        });
        if (!result.ok) return result;
        const replacementKey = result.replacementKeycard
          ? {
              id: result.replacementKeycard.id,
              stayId: result.replacementKeycard.stayId,
              guestId: result.replacementKeycard.guestId,
              roomId: result.replacementKeycard.roomId,
              status: "active" as const,
              validFrom: nowIso(),
              validTo: state.stays.find((entry) => entry.id === input.stayId)?.departure ?? nowIso().slice(0, 10),
            }
          : null;
        setState((prev) => {
          const stay = prev.stays.find((entry) => entry.id === input.stayId);
          if (!stay) return prev;
          return {
            ...prev,
            stays: prev.stays.map((entry) => (entry.id === input.stayId ? { ...entry, roomId: input.toRoomId } : entry)),
            reservations: prev.reservations.map((entry) => (entry.id === stay.reservationId ? { ...entry, roomId: input.toRoomId } : entry)),
            keys: [
              ...(replacementKey ? [replacementKey] : []),
              ...prev.keys.map((entry) => (entry.stayId === input.stayId ? { ...entry, status: "revoked" as const } : entry)),
            ],
            frontDeskRoomMoves: result.roomMove ? [result.roomMove, ...prev.frontDeskRoomMoves] : prev.frontDeskRoomMoves,
            frontDeskAudit: [...(result.auditEntries ?? []), ...prev.frontDeskAudit],
          };
        });
        emit("RoomMoved", { stayId: input.stayId, roomId: input.toRoomId });
        if (replacementKey) emit("KeyIssued", { stayId: input.stayId, keyId: replacementKey.id });
        return { ok: true, roomMove: result.roomMove, key: replacementKey, auditEntries: result.auditEntries };
      },
      completeFrontDeskCheckout: async (input) => {
        const result = await frontDeskEndpoints.completeCheckout(input, buildFrontDeskContext(state), {
          idFactory: id,
          invoiceProvider: frontDeskInvoiceProvider,
        });
        if (!result.ok) {
          if (result.auditEntries?.length) {
            setState((prev) => ({
              ...prev,
              frontDeskAudit: [...result.auditEntries, ...prev.frontDeskAudit],
            }));
          }
          return result;
        }
        setState((prev) => {
          const stay = prev.stays.find((entry) => entry.id === input.stayId);
          if (!stay) return prev;
          const reservationId = stay.reservationId;
          const folio = prev.folios.find((entry) => entry.stayId === input.stayId && entry.status === "open");
          return {
            ...prev,
            stays: prev.stays.map((entry) => (entry.id === input.stayId ? { ...entry, status: "checkedout" } : entry)),
            reservations: prev.reservations.map((entry) => (entry.id === reservationId ? { ...entry, status: "checkedout" } : entry)),
            folios: prev.folios.map((entry) =>
              entry.stayId === input.stayId ? { ...entry, balance: 0, status: "closed", closedAt: nowIso() } : entry
            ),
            payments: result.settlement && folio
              ? [
                  {
                    id: result.settlement.id,
                    folioId: folio.id,
                    amount: result.settlement.amount,
                    currency: result.settlement.currency as Folio["currency"],
                    method: result.settlement.method,
                    status: "captured",
                    ts: result.settlement.createdAt,
                    allocatedAmount: result.settlement.amount,
                    refundedAmount: 0,
                  },
                  ...prev.payments,
                ]
              : prev.payments,
            keys: prev.keys.map((entry) => (entry.stayId === input.stayId ? { ...entry, status: "revoked" } : entry)),
            frontDeskInvoices: result.invoice ? [result.invoice, ...prev.frontDeskInvoices] : prev.frontDeskInvoices,
            frontDeskSettlements: result.settlement ? [result.settlement, ...prev.frontDeskSettlements] : prev.frontDeskSettlements,
            frontDeskAudit: [...(result.auditEntries ?? []), ...prev.frontDeskAudit],
          };
        });
        emit("CheckoutSettled", { stayId: input.stayId });
        emit("CheckOutCompleted", { stayId: input.stayId });
        emit("InvoiceGenerated", { stayId: input.stayId, invoiceId: result.invoice?.id });
        return result;
      },
      createGroupReservation: (input) => {
        const reservationId = id("RG");
        const eventIds = input.linkedEvents.map((event) => event.id || id("GE"));
        const groupEvents: GroupEvent[] = input.linkedEvents.map((event, index) => ({
          id: eventIds[index],
          groupReservationId: reservationId,
          eventName: event.eventName.trim(),
          eventType: (event.eventType || "other") as GroupEvent["eventType"],
          description: event.description.trim() || undefined,
          startDate: event.startDate,
          endDate: event.endDate,
          startTime: event.startTime || undefined,
          endTime: event.endTime || undefined,
          attendeeCount: event.attendeeCount ? Number(event.attendeeCount) : undefined,
          organizerName: event.organizerName.trim() || undefined,
          organizerContact: event.organizerContact.trim() || undefined,
          notes: event.notes.trim() || undefined,
          status: event.status,
          linked: true,
        }));
        const primaryEvent = groupEvents.find((event) => event.id === input.primaryEventId) ?? groupEvents[0];
        const groupReservation: GroupReservation = {
          id: reservationId,
          guestId: input.primaryGuestId,
          primaryGuestId: input.primaryGuestId,
          arrival: input.arrival,
          departure: input.departure,
          adults: 1,
          children: 0,
          rooms: input.rooms.length,
          ratePlanId: input.ratePlanId ?? "bar",
          roomTypeId: input.rooms[0]?.roomTypeId ?? "rt-std",
          status: "booked",
          createdAt: nowIso(),
          source: input.source ?? "Direct",
          reservationType: "group",
          groupName: input.groupName,
          groupId: reservationId,
          masterReservationId: reservationId,
          billingMode: input.billingMode,
          hasLinkedEvents: groupEvents.length > 0,
          linkedEventIds: groupEvents.map((event) => event.id),
          primaryEventId: primaryEvent?.id,
          eventSummary: primaryEvent
            ? {
                eventName: primaryEvent.eventName,
                eventType: primaryEvent.eventType,
                status: primaryEvent.status,
                startDate: primaryEvent.startDate,
                endDate: primaryEvent.endDate,
                attendeeCount: primaryEvent.attendeeCount,
                organizerName: primaryEvent.organizerName,
              }
            : undefined,
          groupedRooms: input.rooms.map((room) => ({
            id: room.id || id("GR"),
            roomTypeId: room.roomTypeId,
            arrival: room.arrival,
            departure: room.departure,
            guest: room.guest,
            status: "booked",
          })),
        };
        const billingAccount = createGroupBillingAccount(groupReservation, input.billingMode);
        setState((prev) => ({
          ...prev,
          reservations: [groupReservation, ...prev.reservations],
          groupEvents: [...groupEvents, ...prev.groupEvents],
          folios: [...billingAccount.folios, ...prev.folios],
          reservationHistory: addReservationHistoryEntry(prev.reservationHistory, {
            reservationId,
            action: "group_reservation_created",
            oldStatus: "booked",
            newStatus: "booked",
            reason: `Group ${input.groupName} created with ${input.rooms.length} rooms.`,
          }).entries,
        }));
        emit("ReservationCreated", { reservationId });
        return groupReservation;
      },
      updateGroupReservation: (reservationId, input) => {
        let snapshot: ReservationUpdateSnapshot | null = null;
        setState((prev) => {
          const existingReservation = prev.reservations.find((reservation) => reservation.id === reservationId);
          if (!existingReservation || existingReservation.reservationType !== "group") return prev;
          const nextEvents: GroupEvent[] = input.linkedEvents.map((event) => ({
            id: event.id || id("GE"),
            groupReservationId: reservationId,
            eventName: event.eventName.trim(),
            eventType: (event.eventType || "other") as GroupEvent["eventType"],
            description: event.description.trim() || undefined,
            startDate: event.startDate,
            endDate: event.endDate,
            startTime: event.startTime || undefined,
            endTime: event.endTime || undefined,
            attendeeCount: event.attendeeCount ? Number(event.attendeeCount) : undefined,
            organizerName: event.organizerName.trim() || undefined,
            organizerContact: event.organizerContact.trim() || undefined,
            notes: event.notes.trim() || undefined,
            status: event.status,
            linked: true,
          }));
          const primaryEvent = nextEvents.find((event) => event.id === input.primaryEventId) ?? nextEvents[0];
          snapshot = {
            reservations: prev.reservations,
            historyEntries: prev.reservationHistory.filter((entry) => entry.reservationId === reservationId),
            folios: prev.folios,
            groupEvents: prev.groupEvents,
          };
          const nextReservation: GroupReservation = {
            ...existingReservation,
            reservationType: "group",
            guestId: input.primaryGuestId,
            primaryGuestId: input.primaryGuestId,
            arrival: input.arrival,
            departure: input.departure,
            rooms: input.rooms.length,
            roomTypeId: input.rooms[0]?.roomTypeId ?? existingReservation.roomTypeId,
            groupName: input.groupName,
            billingMode: input.billingMode,
            hasLinkedEvents: nextEvents.length > 0,
            linkedEventIds: nextEvents.map((event) => event.id),
            primaryEventId: primaryEvent?.id,
            eventSummary: primaryEvent
              ? {
                  eventName: primaryEvent.eventName,
                  eventType: primaryEvent.eventType,
                  status: primaryEvent.status,
                  startDate: primaryEvent.startDate,
                  endDate: primaryEvent.endDate,
                  attendeeCount: primaryEvent.attendeeCount,
                  organizerName: primaryEvent.organizerName,
                }
              : undefined,
            groupedRooms: input.rooms.map((room) => ({
              id: room.id || id("GR"),
              roomTypeId: room.roomTypeId,
              arrival: room.arrival,
              departure: room.departure,
              guest: room.guest,
              status: existingReservation.status,
            })),
          };
          const billingAccount = createGroupBillingAccount(nextReservation, input.billingMode);
          return {
            ...prev,
            reservations: prev.reservations.map((reservation) => reservation.id === reservationId ? nextReservation : reservation),
            groupEvents: [
              ...prev.groupEvents.filter((event) => event.groupReservationId !== reservationId),
              ...nextEvents,
            ],
            folios: [
              ...prev.folios.filter((folio) => folio.groupReservationId !== reservationId),
              ...billingAccount.folios,
            ],
          };
        });
        if (snapshot) emit("ReservationUpdated", { reservationId });
        return snapshot;
      },
      updateReservation: (reservationId, input) => {
        let snapshot: ReservationUpdateSnapshot | null = null;
        setState((prev) => {
          const existingReservation = prev.reservations.find((reservation) => reservation.id === reservationId);
          if (!existingReservation) return prev;
          const existingGuest = prev.guests.find((guest) => guest.id === existingReservation.guestId);
          if (!existingGuest) return prev;
          const existingStay = prev.stays.find((stay) => stay.reservationId === reservationId);
          snapshot = {
            reservations: prev.reservations,
            guest: existingGuest,
            stay: existingStay,
            historyEntries: prev.reservationHistory.filter((entry) => entry.reservationId === reservationId),
            folios: prev.folios,
          };

          return {
            ...prev,
            reservations: prev.reservations.map((reservation) =>
              reservation.id === reservationId
                ? {
                    ...reservation,
                    arrival: input.arrival,
                    departure: input.departure,
                    roomTypeId: input.roomTypeId,
                  }
                : reservation
            ),
            guests: prev.guests.map((guest) =>
              guest.id === existingGuest.id ? { ...guest, ...normalizeMandatoryGuestInfo(input.guest) } : guest
            ),
            stays: prev.stays.map((stay) =>
              stay.reservationId === reservationId
                ? { ...stay, arrival: input.arrival, departure: input.departure }
                : stay
            ),
          };
        });
        if (snapshot) {
          emit("ReservationUpdated", { reservationId });
        }
        return snapshot;
      },
      cancelReservation: (reservationId, input) => {
        let snapshot: ReservationUpdateSnapshot | null = null;
        setState((prev) => {
          const existingReservation = prev.reservations.find((reservation) => reservation.id === reservationId);
          if (!existingReservation) return prev;

          snapshot = {
            reservations: prev.reservations,
            historyEntries: prev.reservationHistory.filter((entry) => entry.reservationId === reservationId),
            folios: prev.folios,
            groupEvents: prev.groupEvents,
          };

          const cancelledReservation = buildCancelledReservation(existingReservation, input.reason, input.actor);
          const historyResult = addReservationHistoryEntry(prev.reservationHistory, {
            reservationId,
            action: "reservation_cancelled",
            oldStatus: existingReservation.status,
            newStatus: "cancelled",
            reason: input.reason,
            actor: input.actor,
            timestamp: cancelledReservation.cancelledAt,
          });

          return {
            ...prev,
            reservations: prev.reservations.map((reservation) =>
              reservation.id === reservationId ? cancelledReservation : reservation
            ),
            groupEvents: prev.groupEvents.map((event) =>
              event.groupReservationId === reservationId ? { ...event, status: "cancelled" } : event
            ),
            reservationHistory: historyResult.entries,
          };
        });
        if (snapshot) {
          emit("ReservationCancelled", { reservationId });
          emit("ReservationUpdated", { reservationId });
        }
        return snapshot;
      },
      applyEarlyCheckIn: (reservationId, input) => {
        let snapshot: ReservationUpdateSnapshot | null = null;
        setState((prev) => {
          const reservation = prev.reservations.find((entry) => entry.id === reservationId);
          if (!reservation) return prev;
          const timestamp = nowIso();
          const folio = prev.folios.find((entry) => entry.reservationId === reservationId);
          const historyResult = addReservationHistoryEntry(prev.reservationHistory, {
            reservationId,
            action: "early_checkin_applied",
            oldStatus: reservation.status,
            newStatus: reservation.status,
            reason: `Early check-in approved for ${input.requestedTime}.`,
            actor: input.actor,
            note: input.note,
            timestamp,
          });
          snapshot = {
            reservations: prev.reservations,
            historyEntries: prev.reservationHistory.filter((entry) => entry.reservationId === reservationId),
            folios: prev.folios,
          };
          return {
            ...prev,
            reservations: prev.reservations.map((entry) =>
              entry.id === reservationId
                ? {
                    ...entry,
                    earlyCheckIn: {
                      approved: true,
                      approvedAt: timestamp,
                      approvedBy: input.actor,
                      requestedTime: input.requestedTime,
                      regularCheckInTime: input.regularCheckInTime,
                      fee: input.fee,
                      note: input.note?.trim() || undefined,
                      folioPosted: input.fee > 0,
                    },
                  }
                : entry
            ),
            folios: folio && input.fee > 0
              ? prev.folios.map((entry) => (entry.id === folio.id ? { ...entry, balance: entry.balance + input.fee, totalCharges: entry.totalCharges + input.fee, status: "open" } : entry))
              : prev.folios,
            postings: folio && input.fee > 0
              ? [
                  {
                    id: id("TX"),
                    folioId: folio.id,
                    source: "pms",
                    category: "other",
                    amount: input.fee,
                    currency: folio.currency,
                    description: "Early check-in fee",
                    postedAt: timestamp,
                    status: "posted",
                  },
                  ...prev.postings,
                ]
              : prev.postings,
            reservationHistory: historyResult.entries,
          };
        });
        if (snapshot) emit("ReservationUpdated", { reservationId });
        return snapshot;
      },
      applyLateCheckOut: (stayId, input) => {
        let snapshot: ReservationUpdateSnapshot | null = null;
        let reservationIdForEmit = "";
        setState((prev) => {
          const stay = prev.stays.find((entry) => entry.id === stayId);
          if (!stay) return prev;
          const reservation = prev.reservations.find((entry) => entry.id === stay.reservationId);
          if (!reservation) return prev;
          reservationIdForEmit = reservation.id;
          const timestamp = nowIso();
          const folio = prev.folios.find((entry) => entry.stayId === stayId) ?? prev.folios.find((entry) => entry.reservationId === reservation.id);
          const historyResult = addReservationHistoryEntry(prev.reservationHistory, {
            reservationId: reservation.id,
            action: "late_checkout_applied",
            oldStatus: reservation.status,
            newStatus: reservation.status,
            reason: `Late check-out approved until ${input.extendedDepartureTime}.`,
            actor: input.actor,
            note: input.note,
            timestamp,
          });
          snapshot = {
            reservations: prev.reservations,
            stay,
            historyEntries: prev.reservationHistory.filter((entry) => entry.reservationId === reservation.id),
            folios: prev.folios,
          };
          return {
            ...prev,
            reservations: prev.reservations.map((entry) =>
              entry.id === reservation.id
                ? {
                    ...entry,
                    lateCheckOut: {
                      approved: true,
                      approvedAt: timestamp,
                      approvedBy: input.actor,
                      extendedDepartureTime: input.extendedDepartureTime,
                      regularCheckOutTime: input.regularCheckOutTime,
                      fee: input.fee,
                      note: input.note?.trim() || undefined,
                      folioPosted: input.fee > 0,
                    },
                  }
                : entry
            ),
            stays: prev.stays.map((entry) =>
              entry.id === stayId
                ? {
                    ...entry,
                    regularCheckOutTime: input.regularCheckOutTime,
                    lateCheckOut: {
                      approved: true,
                      approvedAt: timestamp,
                      approvedBy: input.actor,
                      extendedDepartureTime: input.extendedDepartureTime,
                      regularCheckOutTime: input.regularCheckOutTime,
                      fee: input.fee,
                      note: input.note?.trim() || undefined,
                      folioPosted: input.fee > 0,
                    },
                  }
                : entry
            ),
            folios: folio && input.fee > 0
              ? prev.folios.map((entry) => (entry.id === folio.id ? { ...entry, balance: entry.balance + input.fee, totalCharges: entry.totalCharges + input.fee, status: "open" } : entry))
              : prev.folios,
            postings: folio && input.fee > 0
              ? [
                  {
                    id: id("TX"),
                    folioId: folio.id,
                    source: "pms",
                    category: "other",
                    amount: input.fee,
                    currency: folio.currency,
                    description: "Late check-out fee",
                    postedAt: timestamp,
                    status: "posted",
                  },
                  ...prev.postings,
                ]
              : prev.postings,
            reservationHistory: historyResult.entries,
          };
        });
        if (snapshot) {
          emit("ReservationUpdated", { reservationId: reservationIdForEmit, stayId });
        }
        return snapshot;
      },
      assignReservationRoom: (reservationId, roomId, groupRoomId) => {
        let snapshot: ReservationUpdateSnapshot | null = null;
        setState((prev) => {
          const existingReservation = prev.reservations.find((reservation) => reservation.id === reservationId);
          if (!existingReservation) return prev;

          snapshot = {
            reservations: prev.reservations,
            stay: prev.stays.find((stay) => stay.reservationId === reservationId),
            historyEntries: prev.reservationHistory.filter((entry) => entry.reservationId === reservationId),
            folios: prev.folios,
          };

          const nextReservations = prev.reservations.map((reservation) => {
            if (reservation.id !== reservationId) return reservation;
            if (groupRoomId && reservation.reservationType === "group") {
              return {
                ...reservation,
                groupedRooms: (reservation.groupedRooms ?? []).map((room) =>
                  room.id === groupRoomId ? { ...room, roomId } : room
                ),
              };
            }
            return {
              ...reservation,
              roomId,
            };
          });

          const nextStays = prev.stays.map((stay) =>
            stay.reservationId === reservationId ? { ...stay, roomId } : stay
          );

          return {
            ...prev,
            reservations: nextReservations,
            stays: nextStays,
          };
        });

        if (snapshot) {
          emit("RoomAssigned", { reservationId, roomId, groupRoomId });
          emit("ReservationUpdated", { reservationId });
        }

        return snapshot;
      },
      restoreReservationSnapshot: (snapshot) => {
        setState((prev) => ({
          ...prev,
          reservations: snapshot.reservations,
          guests: snapshot.guest
            ? prev.guests.map((guest) => (guest.id === snapshot.guest?.id ? snapshot.guest : guest))
            : prev.guests,
          stays: snapshot.stay
            ? prev.stays.map((stay) => (stay.id === snapshot.stay?.id ? snapshot.stay : stay))
            : prev.stays,
          folios: snapshot.folios ?? prev.folios,
          groupEvents: snapshot.groupEvents ?? prev.groupEvents,
          reservationHistory: [
            ...prev.reservationHistory.filter((entry) =>
              !snapshot.reservations.some((reservation) => reservation.id === entry.reservationId)
            ),
            ...(snapshot.historyEntries ?? []),
          ].sort((left, right) => right.timestamp.localeCompare(left.timestamp)),
        }));
      },
      startCheckin: (reservationId) => {
        setState((prev) => ({
          ...prev,
          reservations: prev.reservations.map((r) =>
            r.id === reservationId ? { ...r, status: "checkin" } : r
          ),
        }));
        emit("CheckInStarted", { reservationId });
        emit("ReservationUpdated", { reservationId });
      },
      completeCheckin: (reservationId, roomId) => {
        const createdRef = { current: null as Stay | null };
        setState((prev) => {
          const res = prev.reservations.find((r) => r.id === reservationId);
          if (!res) return prev;
          const created: Stay = {
            id: id("ST"),
            reservationId,
            guestId: res.guestId,
            roomId,
            arrival: res.arrival,
            departure: res.departure,
            status: "inhouse",
          };
          createdRef.current = created;
          const updatedReservations: Reservation[] = prev.reservations.map((r) =>
            r.id === reservationId ? ({ ...r, status: "inhouse", roomId } as Reservation) : r
          );
          const updatedFolios = prev.folios.map((f) =>
            f.reservationId === reservationId ? { ...f, stayId: created.id } : f
          );
          return {
            ...prev,
            reservations: updatedReservations,
            stays: [created, ...prev.stays],
            folios: updatedFolios,
          };
        });
        const created = createdRef.current;
        if (!created) return null;
        emit("CheckInCompleted", { reservationId, stayId: created.id });
        emit("RoomAssigned", { reservationId, roomId });
        return created;
      },
      checkoutStay: (stayId) => {
        setState((prev) => {
          const stay = prev.stays.find((s) => s.id === stayId);
          if (!stay) return prev;
          return {
            ...prev,
            stays: prev.stays.map((s) => (s.id === stayId ? { ...s, status: "checkedout" } : s)),
            reservations: prev.reservations.map((r) =>
              r.id === stay.reservationId ? { ...r, status: "checkedout" } : r
            ),
            folios: prev.folios.map((f) =>
              f.stayId === stayId ? { ...f, status: "closed" } : f
            ),
            keys: prev.keys.map((k) => (k.stayId === stayId ? { ...k, status: "revoked" } : k)),
          };
        });
        emit("CheckOutCompleted", { stayId });
        emit("KeyRevoked", { stayId });
      },
      issueKey: (stayId) => {
        const keyRef = { current: null as KeyCredential | null };
        setState((prev) => {
          const stay = prev.stays.find((s) => s.id === stayId);
          if (!stay) return prev;
          const key: KeyCredential = {
            id: id("K"),
            stayId,
            guestId: stay.guestId,
            roomId: stay.roomId,
            status: "active",
            validFrom: stay.arrival,
            validTo: stay.departure,
          };
          keyRef.current = key;
          return { ...prev, keys: [key, ...prev.keys] };
        });
        const key = keyRef.current;
        if (key) emit("KeyIssued", { stayId, keyId: key.id });
        return key;
      },
      revokeKeysByStay: (stayId) => {
        setState((prev) => ({
          ...prev,
          keys: prev.keys.map((k) => (k.stayId === stayId ? { ...k, status: "revoked" } : k)),
        }));
        emit("KeyRevoked", { stayId });
      },
      applyKeycoderState: (input) => {
        setState((prev) => {
          const nextKeycoderKeycards = input.keycards ?? prev.keycoderKeycards;
          const mappedHosKeys = mapKeycoderKeycardsToHosKeys(nextKeycoderKeycards);
          const mappedKeyIds = new Set(mappedHosKeys.map((entry) => entry.id));

          return {
            ...prev,
            keycoderKeycards: nextKeycoderKeycards,
            keycoderOperationLogs: input.operationLogs ?? prev.keycoderOperationLogs,
            keycoderEncoderDevices: input.encoderDevices ?? prev.keycoderEncoderDevices,
            keys: [
              ...mappedHosKeys,
              ...prev.keys.filter((entry) => !mappedKeyIds.has(entry.id)),
            ],
          };
        });
      },
      appendKeycoderOperationLog: (log) => {
        setState((prev) => ({
          ...prev,
          keycoderOperationLogs: [log, ...prev.keycoderOperationLogs],
        }));
      },
      setKeycoderAccessCatalog: (catalog) => {
        setState((prev) => ({
          ...prev,
          keycoderAccessCatalog: catalog,
        }));
      },
      createParkingSession: (input) => {
        const session: ParkingSession = {
          ...input,
          id: id("P"),
          status: "active",
          postedToFolio: false,
          paid: false,
        };
        setState((prev) => ({ ...prev, parkingSessions: [session, ...prev.parkingSessions] }));
        emit("ParkingSessionCreated", { sessionId: session.id });
        return session;
      },
      closeParkingSession: (sessionId) => {
        setState((prev) => ({
          ...prev,
          parkingSessions: prev.parkingSessions.map((s) =>
            s.id === sessionId ? { ...s, status: "closed" } : s
          ),
        }));
        emit("ParkingSessionClosed", { sessionId });
      },
      createHousekeepingTask: (input) => {
        const result = housekeepingEndpoints.createTask(input, buildHousekeepingContext(state), { idFactory: id });
        if (!result.ok) return result;
        setState((prev) => ({
          ...prev,
          housekeepingTasks: [result.task, ...prev.housekeepingTasks],
          housekeepingAudit: [result.auditEntry, ...prev.housekeepingAudit],
          housekeepingRealtimeFeed: [result.realtimeEnvelope, ...prev.housekeepingRealtimeFeed].slice(0, 30),
        }));
        housekeepingRealtimeGateway.publish(result.realtimeEnvelope);
        emit("HousekeepingTaskCreated", { taskId: result.task.id, roomId: result.task.roomId });
        return result;
      },
      updateHousekeepingTask: (input) => {
        const result = housekeepingEndpoints.updateTask(input, buildHousekeepingContext(state), { idFactory: id });
        if (!result.ok) return result;
        setState((prev) => ({
          ...prev,
          housekeepingTasks: prev.housekeepingTasks.map((task) => (task.id === result.task.id ? result.task : task)),
          rooms: prev.rooms.map((room) => (room.id === result.room.id ? result.room : room)),
          housekeepingAudit: [result.auditEntry, ...prev.housekeepingAudit],
          housekeepingRealtimeFeed: [result.realtimeEnvelope, ...prev.housekeepingRealtimeFeed].slice(0, 30),
        }));
        housekeepingRealtimeGateway.publish(result.realtimeEnvelope);
        emit("HousekeepingTaskUpdated", { taskId: result.task.id, roomId: result.task.roomId, status: result.task.status });
        if (result.room.id === input.roomId) {
          emit("RoomStatusUpdated", { roomId: result.room.id, status: result.room.status, revision: result.room.revision });
        }
        return result;
      },
      updateRoomState: (input) => {
        const result = housekeepingEndpoints.updateRoomState(input, buildHousekeepingContext(state), { idFactory: id });
        if (!result.ok) return result;
        setState((prev) => ({
          ...prev,
          rooms: prev.rooms.map((room) => (room.id === result.room.id ? result.room : room)),
          housekeepingAudit: [result.auditEntry, ...prev.housekeepingAudit],
          housekeepingRealtimeFeed: [result.realtimeEnvelope, ...prev.housekeepingRealtimeFeed].slice(0, 30),
        }));
        housekeepingRealtimeGateway.publish(result.realtimeEnvelope);
        emit("RoomStatusUpdated", { roomId: result.room.id, status: result.room.status, revision: result.room.revision });
        return result;
      },
      reportMaintenanceIssue: (input) => {
        const result = housekeepingEndpoints.reportMaintenance(input, buildHousekeepingContext(state), { idFactory: id });
        if (!result.ok) return result;
        setState((prev) => ({
          ...prev,
          maintenanceReports: [result.report, ...prev.maintenanceReports],
          rooms: prev.rooms.map((room) => (room.id === result.room.id ? result.room : room)),
          housekeepingAudit: [result.auditEntry, ...prev.housekeepingAudit],
          housekeepingRealtimeFeed: [result.realtimeEnvelope, ...prev.housekeepingRealtimeFeed].slice(0, 30),
        }));
        housekeepingRealtimeGateway.publish(result.realtimeEnvelope);
        emit("MaintenanceReported", { reportId: result.report.id, roomId: result.report.roomId });
        emit("RoomStatusUpdated", { roomId: result.room.id, status: result.room.status, revision: result.room.revision });
        return result;
      },
      updateMaintenanceReport: (input) => {
        const result = housekeepingEndpoints.updateMaintenance(input, buildHousekeepingContext(state), { idFactory: id });
        if (!result.ok) return result;
        setState((prev) => ({
          ...prev,
          maintenanceReports: prev.maintenanceReports.map((report) => (report.id === result.report.id ? result.report : report)),
          rooms: prev.rooms.map((room) => (room.id === result.room.id ? result.room : room)),
          housekeepingAudit: [result.auditEntry, ...prev.housekeepingAudit],
          housekeepingRealtimeFeed: [result.realtimeEnvelope, ...prev.housekeepingRealtimeFeed].slice(0, 30),
        }));
        housekeepingRealtimeGateway.publish(result.realtimeEnvelope);
        emit("MaintenanceUpdated", { reportId: result.report.id, roomId: result.report.roomId, status: result.report.status });
        emit("RoomStatusUpdated", { roomId: result.room.id, status: result.room.status, revision: result.room.revision });
        return result;
      },
      blockRoom: (input) => {
        const result = housekeepingEndpoints.blockRoom(input, buildHousekeepingContext(state), { idFactory: id });
        if (!result.ok) return result;
        setState((prev) => ({
          ...prev,
          roomBlocks: [result.block, ...prev.roomBlocks.filter((block) => block.id !== result.block.id)],
          rooms: prev.rooms.map((room) => (room.id === result.room.id ? result.room : room)),
          housekeepingAudit: [result.auditEntry, ...prev.housekeepingAudit],
          housekeepingRealtimeFeed: [result.realtimeEnvelope, ...prev.housekeepingRealtimeFeed].slice(0, 30),
        }));
        housekeepingRealtimeGateway.publish(result.realtimeEnvelope);
        emit("RoomBlocked", { blockId: result.block.id, roomId: result.block.roomId });
        emit("RoomStatusUpdated", { roomId: result.room.id, status: result.room.status, revision: result.room.revision });
        return result;
      },
      releaseRoomBlock: (input) => {
        const result = housekeepingEndpoints.releaseRoomBlock(input, buildHousekeepingContext(state), { idFactory: id });
        if (!result.ok) return result;
        setState((prev) => ({
          ...prev,
          roomBlocks: prev.roomBlocks.map((block) => (block.id === result.block.id ? result.block : block)),
          rooms: prev.rooms.map((room) => (room.id === result.room.id ? result.room : room)),
          housekeepingAudit: [result.auditEntry, ...prev.housekeepingAudit],
          housekeepingRealtimeFeed: [result.realtimeEnvelope, ...prev.housekeepingRealtimeFeed].slice(0, 30),
        }));
        housekeepingRealtimeGateway.publish(result.realtimeEnvelope);
        emit("RoomBlockReleased", { blockId: result.block.id, roomId: result.block.roomId });
        emit("RoomStatusUpdated", { roomId: result.room.id, status: result.room.status, revision: result.room.revision });
        return result;
      },
      createBillingFolio: (input) => {
        const result = billingManagementEndpoints.createFolio(input, buildBillingContext(state), { idFactory: id });
        if (!result.ok) return result;
        setState((prev) => ({
          ...prev,
          folios: [result.folio, ...prev.folios.filter((folio) => folio.id !== result.folio.id)],
          billingAudit: [...result.auditEntries, ...prev.billingAudit],
        }));
        return result;
      },
      postBillingCharge: (input) => {
        const result = billingManagementEndpoints.postCharge(input, buildBillingContext(state), { idFactory: id });
        if (!result.ok) return result;
        setState((prev) => ({
          ...prev,
          folios: prev.folios.map((folio) => (folio.id === result.folio.id ? result.folio : folio)),
          postings: [result.item, ...prev.postings],
          billingAudit: [...result.auditEntries, ...prev.billingAudit],
        }));
        emit("FolioPosted", { postingId: result.item.id, folioId: input.folioId });
        return result;
      },
      settleBillingFolio: (input) => {
        const result = billingManagementEndpoints.settleFolio(input, buildBillingContext(state), { idFactory: id });
        if (!result.ok) return result;
        setState((prev) => ({
          ...prev,
          folios: prev.folios.map((folio) => (folio.id === result.folio.id ? { ...result.folio, closedAt: result.folio.status === "closed" ? nowIso() : folio.closedAt } : folio)),
          payments: [...result.payments, ...prev.payments],
          billingAudit: [...result.auditEntries, ...prev.billingAudit],
        }));
        return result;
      },
      createBillingRefund: (input) => {
        const result = billingManagementEndpoints.refundPayment(input, buildBillingContext(state), { idFactory: id });
        if (!result.ok) return result;
        setState((prev) => ({
          ...prev,
          folios: prev.folios.map((folio) => (folio.id === result.folio.id ? result.folio : folio)),
          payments: prev.payments.map((payment) => (payment.id === result.payment.id ? result.payment : payment)),
          refunds: [result.refund, ...prev.refunds],
          billingAudit: [...result.auditEntries, ...prev.billingAudit],
        }));
        return result;
      },
      createBillingAdjustment: (input) => {
        const result = billingManagementEndpoints.adjustFolio(input, buildBillingContext(state), { idFactory: id });
        if (!result.ok) return result;
        setState((prev) => ({
          ...prev,
          folios: prev.folios.map((folio) => (folio.id === result.folio.id ? result.folio : folio)),
          adjustments: [result.adjustment, ...prev.adjustments],
          postings: [result.item, ...prev.postings],
          billingAudit: [...result.auditEntries, ...prev.billingAudit],
        }));
        emit("FolioPosted", { postingId: result.item.id, folioId: input.folioId });
        return result;
      },
      generateBillingInvoice: async (input) => {
        const result = await billingManagementEndpoints.generateInvoice(input, buildBillingContext(state), {
          idFactory: id,
          invoiceProvider: billingInvoiceProvider,
          fiscalProvider: fiscalIntegrationProvider,
        });
        if (!result.ok) return result;
        setState((prev) => ({
          ...prev,
          billingInvoices: [result.invoice, ...prev.billingInvoices],
          fiscalDocuments: result.fiscalDocument ? [result.fiscalDocument, ...prev.fiscalDocuments] : prev.fiscalDocuments,
          billingAudit: [...(result.auditEntries ?? []), ...prev.billingAudit],
        }));
        emit("InvoiceGenerated", { invoiceId: result.invoice.id, folioId: result.invoice.folioId });
        return result;
      },
      pullChannelReservations: async (connectionId, actor) => {
        const provider = channelProviders[connectionId];
        if (!provider) {
          return { ok: false, imported: 0, skipped: 0, failed: 0, message: "Channel provider is not configured." };
        }
        const pulled = await provider.pullReservations();
        let imported = 0;
        let skipped = 0;
        let failed = 0;
        pulled.forEach((reservation) => {
          const result = actions.importChannelReservation({
            connectionId,
            reservation,
            actor,
          });
          if (!result.ok) {
            failed += 1;
            return;
          }
          if (result.decision?.reservationAction === "skipped") {
            skipped += 1;
            return;
          }
          imported += 1;
        });
        return {
          ok: true,
          imported,
          skipped,
          failed,
          message: `Imported ${imported}, skipped ${skipped}, failed ${failed}.`,
        };
      },
      importChannelReservation: (input) => {
        const result = channelManagementEndpoints.importReservation(input, buildChannelManagerContext(state), { idFactory: id });
        if (!result.ok) return result;
        if (!result.link || !result.decision || result.decision.reservationAction === "skipped") return result;
        const link = result.link;
        const reservation = result.reservation;
        const decision = result.decision;

        setState((prev) => {
          let guests = prev.guests;
          let reservations = prev.reservations;
          let folios = prev.folios;
          let billingAudit = prev.billingAudit;

          if (reservation) {
            const existingGuest = prev.guests.find((guest) => guest.id === reservation.guestId);
            if (!existingGuest) {
              guests = [
                {
                  id: reservation.guestId,
                  firstName: input.reservation.guestLabel.split(" ")[0] ?? "OTA",
                  lastName: input.reservation.guestLabel.split(" ").slice(1).join(" ") || "Guest",
                  dateOfBirth: "",
                  nationality: "",
                  address: "",
                  documentNumber: "",
                  email: input.reservation.guestEmail ?? `${reservation.guestId}@example.com`,
                  language: "en",
                },
                ...prev.guests,
              ];
            }

            const hasExistingReservation = prev.reservations.some((entry) => entry.id === reservation.id);
            reservations = hasExistingReservation
              ? prev.reservations.map((entry) => (entry.id === reservation.id ? reservation : entry))
              : [reservation, ...prev.reservations];

            if (!prev.folios.some((folio) => folio.reservationId === reservation.id)) {
              const folio = buildReservationFolio(reservation);
              folios = [folio, ...prev.folios];
              billingAudit = [buildReservationFolioAuditEntry(folio, input.actor), ...prev.billingAudit];
            }
          }

          return {
            ...prev,
            guests,
            reservations,
            folios,
            billingAudit,
            channelReservationLinks: [
              link,
              ...prev.channelReservationLinks.filter((entry) => entry.id !== link.id),
            ],
          };
        });

        if (decision.reservationAction === "created") {
          emit("ChannelReservationImported", { reservationId: reservation?.id, externalReservationId: link.externalReservationId });
        }
        if (decision.reservationAction === "updated") {
          emit("ChannelReservationUpdated", { reservationId: reservation?.id, externalReservationId: link.externalReservationId });
        }
        if (decision.reservationAction === "cancelled") {
          emit("ChannelReservationCancelled", { reservationId: reservation?.id, externalReservationId: link.externalReservationId });
        }
        return result;
      },
      syncChannelRatesAndAvailability: async (input) => {
        const provider = channelProviders[input.connectionId];
        if (!provider) {
          return { ok: false, code: "PROVIDER_NOT_FOUND", message: "Channel provider is not configured." };
        }
        emit("ChannelSyncRequested", { connectionId: input.connectionId, scope: input.scope });
        const result = await channelManagementEndpoints.syncRatesAndAvailability(
          input,
          {
            ...buildChannelManagerContext(state),
            attempts: state.channelSyncAttempts,
          },
          {
            idFactory: id,
            provider,
          }
        );

        if (!result.ok) {
          setState((prev) => ({
            ...prev,
            channelSyncAttempts: result.attempt
              ? [result.attempt, ...prev.channelSyncAttempts.filter((attempt) => attempt.id !== result.attempt?.id)]
              : prev.channelSyncAttempts,
            channelConnections: prev.channelConnections.map((connection) =>
              connection.id === input.connectionId
                ? {
                    ...connection,
                    status: "degraded",
                    lastFailureAt: nowIso(),
                    lastError: result.message,
                  }
                : connection
            ),
          }));
          emit("ChannelSyncFailed", { connectionId: input.connectionId, scope: input.scope });
          return result;
        }

        setState((prev) => ({
          ...prev,
          channelSyncAttempts: result.attempt
            ? [result.attempt, ...prev.channelSyncAttempts.filter((attempt) => attempt.id !== result.attempt?.id)]
            : prev.channelSyncAttempts,
          channelConnections: prev.channelConnections.map((connection) =>
            connection.id === input.connectionId
              ? {
                  ...connection,
                  status: result.delayed ? "degraded" : "connected",
                  lastSuccessfulSyncAt: !result.delayed ? nowIso() : connection.lastSuccessfulSyncAt,
                  lastError: result.delayed ? "Sync response delayed." : undefined,
                }
              : connection
          ),
        }));
        emit("ChannelSyncCompleted", { connectionId: input.connectionId, scope: input.scope, delayed: result.delayed ?? false });
        return result;
      },
      createWorkOrder: (input) => {
        const slaHours = SLA_BY_PRIORITY[input.priority];
        const workOrder: WorkOrder = {
          ...input,
          id: id("WO"),
          status: "open",
          slaHours,
          createdAt: nowIso(),
          dueAt: addHoursIso(slaHours),
        };
        setState((prev) => {
          let rooms = prev.rooms;
          if (workOrder.roomId) {
            if (workOrder.priority === "critical") {
              rooms = rooms.map((room) =>
                room.id === workOrder.roomId
                  ? applyRoomState({
                      ...room,
                      revision: room.revision + 1,
                      lastUpdatedAt: nowIso(),
                      lastUpdatedBy: input.assignedTo,
                    }, { availabilityStatus: "out_of_order" })
                  : room
              );
            } else if (workOrder.priority === "high") {
              rooms = rooms.map((room) =>
                room.id === workOrder.roomId
                  ? applyRoomState({
                      ...room,
                      revision: room.revision + 1,
                      lastUpdatedAt: nowIso(),
                      lastUpdatedBy: input.assignedTo,
                    }, { availabilityStatus: "blocked" })
                  : room
              );
            }
          }
          return { ...prev, workOrders: [workOrder, ...prev.workOrders], rooms };
        });
        return workOrder;
      },
      updateWorkOrderStatus: (id, status) => {
        setState((prev) => {
          const updated = prev.workOrders.map((w) => (w.id === id ? { ...w, status } : w));
          const changed = prev.workOrders.find((w) => w.id === id);
          const roomId = changed?.roomId;
          let nextRooms = prev.rooms;
          if (roomId) {
            if (status === "waiting_parts") {
              nextRooms = nextRooms.map((room) =>
                room.id === roomId
                  ? applyRoomState({
                      ...room,
                      revision: room.revision + 1,
                      lastUpdatedAt: nowIso(),
                    }, { availabilityStatus: "blocked" })
                  : room
              );
            }
            if (status === "completed" || status === "verified") {
              const hasBlocking = updated.some(
                (w) =>
                  w.roomId === roomId &&
                  w.status !== "completed" &&
                  w.status !== "verified" &&
                  w.priority === "critical"
              );
              if (!hasBlocking) {
                nextRooms = nextRooms.map((room) =>
                  room.id === roomId
                    ? applyRoomState({
                        ...room,
                        revision: room.revision + 1,
                        lastUpdatedAt: nowIso(),
                      }, { housekeepingStatus: "clean", availabilityStatus: "available" })
                    : room
                );
              }
            }
          }
          return { ...prev, workOrders: updated, rooms: nextRooms };
        });
      },
      assignWorkOrder: (id, tech) => {
        setState((prev) => ({
          ...prev,
          workOrders: prev.workOrders.map((w) =>
            w.id === id ? { ...w, assignedTo: tech, status: w.status === "open" ? "assigned" : w.status } : w
          ),
        }));
      },
      postFolio: (input) => {
        const result = billingManagementEndpoints.postCharge(
          {
            folioId: input.folioId,
            category: input.category,
            source: input.source,
            amount: input.amount,
            currency: input.currency,
            description: input.description,
            actor: input.postedBy,
            serviceDate: input.serviceDate,
            metadata: input.metadata,
          },
          buildBillingContext(state),
          { idFactory: id }
        );
        if (!result.ok) {
          throw new Error(result.message ?? "Unable to post folio charge.");
        }
        setState((prev) => ({
          ...prev,
          postings: [result.item, ...prev.postings],
          folios: prev.folios.map((folio) => (folio.id === result.folio.id ? result.folio : folio)),
          billingAudit: [...result.auditEntries, ...prev.billingAudit],
        }));
        emit("FolioPosted", { postingId: result.item.id, folioId: input.folioId });
        return result.item;
      },
      postParkingToFolio: (sessionId, folioId) => {
        let postingId = "";
        setState((prev) => {
          const session = prev.parkingSessions.find((s) => s.id === sessionId);
          if (!session) return prev;
          const amount = session.amount;
          const result = billingManagementEndpoints.postCharge(
            {
              folioId,
              category: "parking",
              source: "parking",
              amount,
              currency: "EUR",
              description: `Parking ${session.zone} ${session.space}`,
            },
            {
              ...buildBillingContext(prev),
              now: nowIso(),
            },
            { idFactory: id }
          );
          if (!result.ok) return prev;
          postingId = result.item.id;
          return {
            ...prev,
            postings: [result.item, ...prev.postings],
            folios: prev.folios.map((folio) => (folio.id === result.folio.id ? result.folio : folio)),
            billingAudit: [...result.auditEntries, ...prev.billingAudit],
            parkingSessions: prev.parkingSessions.map((s) =>
              s.id === sessionId ? { ...s, postedToFolio: true } : s
            ),
          };
        });
        emit("FolioPosted", { postingId, folioId, sessionId });
      },
      addPayment: (input) => {
        const result = billingManagementEndpoints.settleFolio(
          {
            folioId: input.folioId,
            allocations: [{ method: input.method, amount: input.amount, reference: input.reference }],
            currency: input.currency,
          },
          buildBillingContext(state),
          { idFactory: id }
        );
        if (!result.ok) {
          throw new Error(result.message ?? "Unable to record payment.");
        }
        const payment = result.payments[0];
        setState((prev) => ({
          ...prev,
          payments: [...result.payments, ...prev.payments],
          folios: prev.folios.map((folio) => (folio.id === result.folio.id ? { ...result.folio, closedAt: result.folio.status === "closed" ? nowIso() : folio.closedAt } : folio)),
          billingAudit: [...result.auditEntries, ...prev.billingAudit],
        }));
        return payment;
      },
      setRoomStatus: (roomId, status) => {
        setState((prev) => ({
          ...prev,
          rooms: prev.rooms.map((room) => {
            if (room.id !== roomId) return room;
            const availabilityStatus =
              status === "out_of_order"
                ? "out_of_order"
                : status === "blocked"
                  ? "blocked"
                  : status === "out_of_service"
                    ? "out_of_service"
                    : "available";
            const housekeepingStatus = status === "clean" || status === "dirty" || status === "inspected"
              ? status
              : room.housekeepingStatus;
            return applyRoomState({
              ...room,
              revision: room.revision + 1,
              lastUpdatedAt: nowIso(),
            }, {
              housekeepingStatus,
              availabilityStatus,
            });
          }),
        }));
      },
    }),
    [state]
  );

  const value = useMemo(() => ({ state, actions, emit }), [state, actions]);

  return <HosContext.Provider value={value}>{children}</HosContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useHos() {
  const ctx = useContext(HosContext);
  if (!ctx) throw new Error("useHos must be used within HosProvider");
  return ctx;
}

export function HosScopedProvider({ children, state }: { children: React.ReactNode; state: HosState }) {
  const ctx = useHos();
  const value = useMemo(() => ({ ...ctx, state }), [ctx, state]);
  return <HosContext.Provider value={value}>{children}</HosContext.Provider>;
}
